/**
 * Excel Service
 * Handles all Excel operations and data writing
 */

import { CONFIG } from '../config/constants.js';
import * as reportEngine from './reportEngine.js';
import * as reportWriter from './reportWriter.js';

/** Company / profile / group row label — always ends with " (id)" for reliable parsing. */
function formatPickLabel(name, id, maxLen = 180) {
  const n = String(name ?? '').trim();
  const sid = String(id ?? '').trim();
  const suffix = ` (${sid})`;
  if (n.length + suffix.length <= maxLen) return n + suffix;
  return `${n.slice(0, Math.max(0, maxLen - suffix.length - 3))}...${suffix}`;
}

/** Parse trailing "(123)" or plain digits; "None" → null. */
function parseTrailingParenId(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (/^none$/i.test(s)) return null;
  const m = s.match(/\((\d+)\)\s*$/);
  if (m) return parseInt(m[1], 10);
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  return null;
}

/** Labels for _PeriodPick column A; values are encoded as "(n)" suffixes for readConfigValues. */
function buildPeriodPickLabels(periodType) {
  const MONTH_NAMES = CONFIG.DATE.MONTH_NAMES;
  const rows = [['Full year (0)']];
  if (periodType === 12) return rows;
  if (periodType === 3) {
    const hints = ['Jan–Mar', 'Apr–Jun', 'Jul–Sep', 'Oct–Dec'];
    for (let q = 1; q <= 4; q++) rows.push([`Q${q} — ${hints[q - 1]} (${q})`]);
    return rows;
  }
  if (periodType === 1) {
    for (let m = 1; m <= 12; m++) rows.push([`${MONTH_NAMES[m - 1]} (${m})`]);
    return rows;
  }
  if (periodType === 6) {
    rows.push(['H1 — Jan–Jun (1)']);
    rows.push(['H2 — Jul–Dec (2)']);
    return rows;
  }
  return rows;
}

function freqToPeriodType(freqStr) {
  const freqMap = { Monthly: 1, Quarterly: 3, 'Half-year': 6, 'Full year': 12 };
  const t = freqStr != null ? String(freqStr).trim() : '';
  return freqMap[t] || parseInt(t, 10) || 12;
}

/**
 * Which financial statement sheets to build from Raw_TrialBalance (trial balance is always built).
 * Driven by _Config / lite “Report” value and legacy {@link allFinancialReports}.
 */
export function resolveFinancialReportFlags(cfg) {
  const pack = String(cfg?.reportPack || '').toLowerCase().trim();
  if (cfg?.allFinancialReports === true || /all\s*financial/.test(pack)) {
    return { profitLoss: true, balanceSheet: true, cashFlow: true };
  }
  if (/profit|p&l|\bpnl\b/.test(pack)) {
    return { profitLoss: true, balanceSheet: false, cashFlow: false };
  }
  if (/balance\s*sheet/.test(pack)) {
    return { profitLoss: false, balanceSheet: true, cashFlow: false };
  }
  if (/cash\s*flow/.test(pack)) {
    return { profitLoss: false, balanceSheet: false, cashFlow: true };
  }
  return { profitLoss: false, balanceSheet: false, cashFlow: false };
}

/**
 * Rpt_* tab to activate after sync so the user lands on the report they chose (lite / _Config).
 */
export function getActivateReportSheetName(cfg) {
  const pack = String(cfg?.reportPack || '').toLowerCase().trim();
  const S = CONFIG.SHEETS;
  if (cfg?.allFinancialReports === true || /all\s*financial/.test(pack)) {
    return S.RPT_PROFIT_LOSS;
  }
  if (/profit|p&l|\bpnl\b/.test(pack)) return S.RPT_PROFIT_LOSS;
  if (/balance\s*sheet/.test(pack)) return S.RPT_BALANCE_SHEET;
  if (/cash\s*flow/.test(pack)) return S.RPT_CASH_FLOW;
  return S.RPT_TRIAL_BALANCE;
}

class ExcelService {
  /**
   * Convert number to Excel column letter (1=A, 2=B, etc.)
   */
  getExcelColumn(num) {
    if (num <= 0) {
      return 'A';
    }
    let result = '';
    let n = num;
    while (n > 0) {
      n--;
      result = String.fromCharCode(65 + (n % 26)) + result;
      n = Math.floor(n / 26);
    }
    return result;
  }
  
  /**
   * Validate Excel range string
   */
  validateRange(startRow, endRow, startCol, endCol) {
    if (startRow <= 0 || endRow <= 0 || startCol <= 0 || endCol <= 0) {
      throw new Error(`Invalid range: rows and columns must be > 0 (${startRow}, ${endRow}, ${startCol}, ${endCol})`);
    }
    if (startRow > endRow || startCol > endCol) {
      throw new Error(`Invalid range: start must be <= end (${startRow}, ${endRow}, ${startCol}, ${endCol})`);
    }
    return true;
  }

  /**
   * Get or create a worksheet
   */
  async getOrCreateWorksheet(context, sheetName) {
    console.log('getOrCreateWorksheet: original sheetName =', sheetName);
    console.log('Original sheet name length:', sheetName?.length);
    
    // Validate and sanitize sheet name - Excel has restrictions
    if (!sheetName || sheetName.length === 0) {
      throw new Error('Sheet name cannot be empty');
    }
    
    // Excel doesn't allow certain characters: \ / ? * [ ]
    const invalidChars = /[\\\/\?\*\[\]]/;
    if (invalidChars.test(sheetName)) {
      // Replace invalid characters with underscore
      sheetName = sheetName.replace(/[\\\/\?\*\[\]]/g, '_');
      console.log('Replaced invalid characters, new name:', sheetName);
    }
    
    // Excel sheet names are limited to 31 characters - truncate if needed
    if (sheetName.length > 31) {
      console.warn(`Sheet name too long (${sheetName.length} chars), truncating to 31 characters`);
      sheetName = sheetName.substring(0, 31);
      console.log('Truncated sheet name:', sheetName);
    }
    
    let worksheet;
    try {
      console.log('Trying to get existing worksheet...');
      worksheet = context.workbook.worksheets.getItem(sheetName);
      console.log('Got worksheet object, activating...');
      worksheet.activate();
      console.log('Activated, syncing...');
      await context.sync();
      console.log('Got existing worksheet successfully');
    } catch (error) {
      console.log('Worksheet not found or error getting it. Error:', error.message, error.name);
      console.log('Creating new worksheet...');
      try {
        worksheet = context.workbook.worksheets.add(sheetName);
        console.log('Added worksheet, activating...');
        worksheet.activate();
        console.log('Activated, syncing...');
        await context.sync();
        console.log('Created new worksheet successfully');
      } catch (createError) {
        console.error('Error creating worksheet:', createError);
        console.error('Error details:', createError.message, createError.name, createError.stack);
        throw new Error(`Failed to create worksheet "${sheetName}": ${createError.message}`);
      }
    }
    return worksheet;
  }

  /**
   * Get the currently active worksheet
   */
  async getCurrentWorksheet(context) {
    const worksheet = context.workbook.worksheets.getActiveWorksheet();
    await context.sync();
    return worksheet;
  }

  /**
   * Clear worksheet if overwrite is enabled
   */
  async clearWorksheet(context, worksheet, overwrite) {
    console.log('clearWorksheet: overwrite =', overwrite);
    if (overwrite) {
      try {
        console.log('Getting used range...');
        const usedRange = worksheet.getUsedRange();
        usedRange.load("address");
        await context.sync();
        
        // Check if usedRange is null (empty worksheet)
        if (usedRange.address) {
          console.log('Used range address:', usedRange.address);
          console.log('Clearing used range...');
          usedRange.clear();
          await context.sync();
          console.log('Worksheet cleared');
        } else {
          console.log('Worksheet is empty, nothing to clear');
        }
      } catch (error) {
        // Worksheet is empty or error getting used range - this is OK
        // getUsedRange() throws an error if the worksheet is completely empty
        console.log('Worksheet appears empty or error getting used range (this is OK):', error.message);
        // Don't rethrow - empty worksheet is fine
      }
    } else {
      console.log('Overwrite disabled, skipping clear');
    }
  }

  /**
   * Write trial balance data to the current active sheet
   * Data structure: Array of { AccountNumber, AccountName, MonthlyBalances: [12 values] }
   * @param {Array} data - Array of account records with monthly balances
   * @param {boolean} overwriteSheets - Whether to overwrite existing data
   * @param {Array<number>} monthsToDisplay - Array of month numbers (1-12) to display. If empty or null, shows all 12 months.
   */
  async writeTrialBalanceToCurrentSheet(data, overwriteSheets, monthsToDisplay = null) {
    try {
      console.log('writeTrialBalanceToCurrentSheet called with data:', data);
      console.log('Data length:', data?.length);
      console.log('First record:', data?.[0]);
      console.log('Months to display:', monthsToDisplay);
      
      await Excel.run(async (context) => {
        const worksheet = await this.getCurrentWorksheet(context);
        await this.clearWorksheet(context, worksheet, overwriteSheets);

        if (!data || data.length === 0) {
          worksheet.getRange("A1").values = [["No data available"]];
          await context.sync();
          return;
        }

        // Month names for headers
        const allMonthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];

        // Determine which months to display
        // If monthsToDisplay is provided and not empty, use it; otherwise show all 12 months
        const displayMonths = (monthsToDisplay && monthsToDisplay.length > 0) 
          ? monthsToDisplay.sort((a, b) => a - b) // Sort ascending
          : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]; // All months
        
        // Get month names for display months (convert 1-based to 0-based index)
        const displayMonthNames = displayMonths.map(monthNum => allMonthNames[monthNum - 1]);
        
        // Find the last month index for YTD calculation (0-based index in monthlyBalances array)
        // YTD should always sum from January (index 0) to the selected month
        // Use the maximum month from displayMonths to determine the YTD range
        // When December (month 12) is selected, YTD should sum all 12 months (indices 0-11)
        const selectedMonth = displayMonths.length > 0 ? Math.max(...displayMonths) : 12; // Get the selected month (1-12), default to 12 if empty
        const lastMonthIndex = selectedMonth - 1; // Convert to 0-based index (0-11)
        console.log(`YTD calculation setup: displayMonths=${JSON.stringify(displayMonths)}, selectedMonth=${selectedMonth}, lastMonthIndex=${lastMonthIndex}`);

        // Build headers: Account Name, then selected months, then YTD Total
        const headers = ['Account Name', ...displayMonthNames, 'YTD Total'];
        const headerRow = 1;
        const dataStartRow = 2;

        // Write headers
        const headerRange = worksheet.getRange(`A${headerRow}:${this.getExcelColumn(headers.length)}${headerRow}`);
        headerRange.values = [headers];
        headerRange.format.font.bold = true;
        headerRange.format.fill.color = CONFIG.EXCEL.HEADER_COLOR;
        headerRange.format.font.color = CONFIG.EXCEL.HEADER_FONT_COLOR;
        await context.sync();

        // Build data rows
        const dataRows = [];
        let grandTotalYtd = 0;
        const monthlyTotals = new Array(displayMonths.length).fill(0);

        for (const record of data) {
          // Handle both camelCase (from API) and PascalCase (legacy) property names
          const accountNumber = record.accountNumber || record.AccountNumber || '';
          const accountName = record.accountName || record.AccountName || '';
          const monthlyBalances = record.monthlyBalances || record.MonthlyBalances || [];
          
          // Skip records without account name
          if (!accountName && !accountNumber) {
            console.warn('Skipping record with no account name or number:', record);
            continue;
          }
          
          // Match Razor page format: "{AccountNumber} - {AccountName}" or just "{AccountName}" if AccountNumber is empty
          const displayName = (!accountNumber || accountNumber.toString().trim() === '')
            ? accountName
            : `${accountNumber} - ${accountName}`;
          
          console.log('Processing record:', { accountNumber, accountName, displayName, monthlyBalancesLength: monthlyBalances?.length });
          
          // Calculate YTD total: sum from January (index 0) to the selected month
          // YTD should always include all months from January to the selected month
          // For example, if December (month 12) is selected, YTD = sum of months 0-11 (all 12 months)
          let ytdTotal = 0;
          if (monthlyBalances && monthlyBalances.length > 0) {
            // Ensure we sum from January (index 0) to the selected month (lastMonthIndex)
            // lastMonthIndex is already 0-based (0-11), so we sum from 0 to lastMonthIndex inclusive
            // When December (month 12) is selected: selectedMonth=12, lastMonthIndex=11, so we sum indices 0-11 (all 12 months)
            // Important: Always sum from index 0 (January) to lastMonthIndex (selected month), not just the selected month
            const endIndex = Math.min(lastMonthIndex, monthlyBalances.length - 1);
            console.log(`YTD calculation: selectedMonth=${selectedMonth}, lastMonthIndex=${lastMonthIndex}, endIndex=${endIndex}, monthlyBalances.length=${monthlyBalances.length}`);
            
            // Sum all months from January (index 0) to the selected month (endIndex)
            for (let i = 0; i <= endIndex; i++) {
              const monthValue = monthlyBalances[i] || 0;
              ytdTotal += monthValue;
            }
            
            // Log the calculation for debugging
            if (selectedMonth === 12) {
              console.log(`YTD for December: Summed months 0-${endIndex} (all 12 months), total=${ytdTotal}`);
            }
          }
          grandTotalYtd += ytdTotal;

          // Extract balances for display months only (convert 1-based month numbers to 0-based array indices)
          const displayBalances = displayMonths.map(monthNum => {
            const index = monthNum - 1; // Convert to 0-based index
            return (index >= 0 && index < monthlyBalances.length) ? (monthlyBalances[index] || 0) : 0;
          });

          // Add to monthly totals
          displayBalances.forEach((balance, index) => {
            monthlyTotals[index] += balance;
          });

          // Build row: Account Name, selected monthly values, YTD Total
          const row = [
            displayName,
            ...displayBalances,
            ytdTotal
          ];
          
          dataRows.push(row);
        }

        // Add totals row
        if (dataRows.length > 0) {
          const totalsRow = ['Total', ...monthlyTotals, grandTotalYtd];
          dataRows.push(totalsRow);
        }

        // Write data rows
        if (dataRows.length > 0) {
          const lastColumn = this.getExcelColumn(headers.length);
          const dataRange = worksheet.getRange(`A${dataStartRow}:${lastColumn}${dataStartRow + dataRows.length - 1}`);
          dataRange.values = dataRows;
          await context.sync();

          // Format totals row (last row)
          const totalsRowIndex = dataStartRow + dataRows.length - 1;
          const totalsRange = worksheet.getRange(`A${totalsRowIndex}:${lastColumn}${totalsRowIndex}`);
          totalsRange.format.font.bold = true;
          await context.sync();
        }

        // Format number columns (skip Account Name column)
        const numberFormat = CONFIG.EXCEL.NUMBER_FORMAT;
        for (let col = 2; col <= headers.length; col++) {
          const colLetter = this.getExcelColumn(col);
          const numberRange = worksheet.getRange(`${colLetter}${dataStartRow}:${colLetter}${dataStartRow + dataRows.length - 1}`);
          numberRange.numberFormat = [[numberFormat]];
        }
        await context.sync();
        
        // Set alignment for number columns - use try-catch to handle potential API differences
        try {
          for (let col = 2; col <= headers.length; col++) {
            const colLetter = this.getExcelColumn(col);
            const numberRange = worksheet.getRange(`${colLetter}${dataStartRow}:${colLetter}${dataStartRow + dataRows.length - 1}`);
            numberRange.format.alignment.horizontal = "Right";
          }
          await context.sync();
        } catch (alignmentError) {
          // Alignment setting failed, but continue - numbers will still be formatted correctly
          console.warn('Could not set alignment:', alignmentError);
        }

        // Set column widths
        worksheet.getRange("A:A").format.columnWidth = CONFIG.EXCEL.COLUMN_WIDTHS.DEFAULT_LABEL;
        for (let col = 2; col <= headers.length; col++) {
          const colLetter = this.getExcelColumn(col);
          worksheet.getRange(`${colLetter}:${colLetter}`).format.columnWidth = CONFIG.EXCEL.COLUMN_WIDTHS.DEFAULT_VALUE;
        }
        await context.sync();

        // Auto-fit columns
        try {
          const usedRange = worksheet.getUsedRange();
          usedRange.format.autofitColumns();
          await context.sync();
        } catch {
          // Ignore autofit errors
        }
      });
    } catch (error) {
      console.error(`Excel writing error:`, error);
      throw new Error(`Failed to write data to Excel: ${error.message}`);
    }
  }

  /**
   * Write trial balance data to Excel in the same format as frontend report
   * Data structure: Array of { AccountNumber, AccountName, MonthlyBalances: [12 values] }
   * @deprecated Use writeTrialBalanceToCurrentSheet instead
   */
  async writeTrialBalanceToExcel(data, sheetName, overwriteSheets) {
    // Redirect to current sheet method for backward compatibility
    return await this.writeTrialBalanceToCurrentSheet(data, overwriteSheets);
  }

  /**
   * Write P&L Report data to Excel in formatted statement layout
   * Supports multiple periods in columns
   */
  async writePnLReportToExcel(pnlDataArray, sheetName, overwriteSheets, numbersIn = 'whole') {
    try {
      await Excel.run(async (context) => {
        const worksheet = await this.getOrCreateWorksheet(context, sheetName);
        
        // Convert single object to array for backward compatibility
        const dataArray = Array.isArray(pnlDataArray) ? pnlDataArray : [pnlDataArray];
        
        if (dataArray.length === 0 || !dataArray[0].sections || dataArray[0].sections.length === 0) {
          worksheet.getRange("A1").values = [["No P&L data available"]];
          await context.sync();
          return;
        }

        // Check if we should overwrite or add/update columns
        if (overwriteSheets) {
          await this.clearWorksheet(context, worksheet, true);
          await this.writePnLMultiColumn(context, worksheet, dataArray, true, numbersIn);
        } else {
          // Add or update specific period columns
          await this.addOrUpdatePnLColumns(context, worksheet, dataArray, numbersIn);
        }
      });
    } catch (error) {
      console.error(`Excel writing error for P&L Report ${sheetName}:`, error);
      throw new Error(`Failed to write P&L Report to Excel for ${sheetName}: ${error.message}`);
    }
  }

  /**
   * Add or update specific P&L columns without overwriting the entire sheet
   */
  async addOrUpdatePnLColumns(context, worksheet, dataArray, numbersIn = 'whole') {
    // Try to read existing headers to find where to place new columns
    let existingPeriods = [];
    let rowStructure = null;
    
    try {
      const usedRange = worksheet.getUsedRange();
      usedRange.load(["values", "rowCount", "columnCount"]);
      await context.sync();
      
      if (usedRange.values && usedRange.values.length > 0) {
        // Read existing period headers (row 3)
        if (usedRange.values.length >= 3) {
          existingPeriods = usedRange.values[2].slice(1).filter(p => p !== ""); // Skip column A
        }
        // Store row structure from column A
        rowStructure = usedRange.values.map(row => row[0]);
      }
    } catch {
      // Sheet is empty, write fresh
      await this.writePnLMultiColumn(context, worksheet, dataArray, true, numbersIn);
      return;
    }

    // For each new period, add or update its column
    for (const pnlData of dataArray) {
      const periodLabel = pnlData.period || `${pnlData.year}-${String(pnlData.month).padStart(2, '0')}`;
      const existingIndex = existingPeriods.indexOf(periodLabel);
      
      if (existingIndex >= 0) {
        // Update existing column (existingIndex + 1 because column A is labels)
        const colLetter = this.getExcelColumn(existingIndex + 2);
        await this.updatePnLColumn(context, worksheet, pnlData, colLetter, rowStructure, numbersIn);
      } else {
        // Add new column at the end
        const newColIndex = existingPeriods.length + 2; // +2 because column A is labels, and we're 1-indexed
        const colLetter = this.getExcelColumn(newColIndex);
        existingPeriods.push(periodLabel);
        await this.addPnLColumn(context, worksheet, pnlData, colLetter, rowStructure, numbersIn);
      }
    }
  }

  /**
   * Update a specific P&L column
   */
  async updatePnLColumn(context, worksheet, pnlData, colLetter, rowStructure, numbersIn = 'whole') {
    const periodLabel = pnlData.period || `${pnlData.year}-${String(pnlData.month).padStart(2, '0')}`;
    
    // Update header
    worksheet.getRange(`${colLetter}3`).values = [[periodLabel]];
    await context.sync();
    
    // Build value map from pnlData
    const valueMap = this.buildPnLValueMap(pnlData);
    
    // Update values starting from row 4
    for (let i = 3; i < rowStructure.length; i++) {
      const rowLabel = rowStructure[i];
      const value = valueMap.get(rowLabel) || '';
      worksheet.getRange(`${colLetter}${i + 1}`).values = [[value]];
    }
    await context.sync();
    
    // Apply number formatting
    const numberRange = worksheet.getRange(`${colLetter}4:${colLetter}${rowStructure.length}`);
    numberRange.numberFormat = [[this.getNumberFormat(numbersIn)]];
    await context.sync();
  }

  /**
   * Add a new P&L column
   */
  async addPnLColumn(context, worksheet, pnlData, colLetter, rowStructure, numbersIn = 'whole') {
    const periodLabel = pnlData.period || `${pnlData.year}-${String(pnlData.month).padStart(2, '0')}`;
    
    // Add header
    worksheet.getRange(`${colLetter}3`).values = [[periodLabel]];
    await context.sync();
    
    // Build value map from pnlData
    const valueMap = this.buildPnLValueMap(pnlData);
    
    // Add values starting from row 4
    for (let i = 3; i < rowStructure.length; i++) {
      const rowLabel = rowStructure[i];
      const value = valueMap.get(rowLabel) || '';
      worksheet.getRange(`${colLetter}${i + 1}`).values = [[value]];
    }
    await context.sync();
    
    // Apply number formatting
    const numberRange = worksheet.getRange(`${colLetter}4:${colLetter}${rowStructure.length}`);
    numberRange.numberFormat = [[this.getNumberFormat(numbersIn)]];
    await context.sync();
    
    // Set column width
    worksheet.getRange(`${colLetter}:${colLetter}`).format.columnWidth = CONFIG.EXCEL.COLUMN_WIDTHS.DEFAULT_VALUE;
    await context.sync();
  }

  /**
   * Build a map of row labels to values from P&L data
   */
  buildPnLValueMap(pnlData) {
    const valueMap = new Map();
    
    if (pnlData.sections) {
      pnlData.sections.forEach(section => {
        this.addSectionToValueMap(valueMap, section, 0);
      });
    }
    
    return valueMap;
  }

  /**
   * Recursively add section values to the map
   */
  addSectionToValueMap(valueMap, section, indentLevel) {
    const indent = '  '.repeat(indentLevel);
    const label = `${indent}${section.name}`;
    valueMap.set(label, section.total || '');
    
    if (section.children && section.children.length > 0) {
      section.children.forEach(child => {
        const childIndent = '  '.repeat(indentLevel + 1);
        const childLabel = `${childIndent}${child.name}`;
        valueMap.set(childLabel, child.amount || 0);
      });
    }
    
    if (section.subSections && section.subSections.length > 0) {
      section.subSections.forEach(subSection => {
        this.addSectionToValueMap(valueMap, subSection, indentLevel + 1);
      });
    }
  }

  /**
   * Write P&L data with multiple periods in columns
   */
  async writePnLMultiColumn(context, worksheet, dataArray, isNewSheet, numbersIn = 'whole') {
    // Use the first dataset to build the row structure
    const firstData = dataArray[0];
    const rows = this.buildPnLRows(firstData);
    
    // Build headers for all periods
    const periodHeaders = dataArray.map(data => 
      data.period || `${data.year}-${String(data.month).padStart(2, '0')}`
    );
    
    // Extract row labels and metadata
    const rowLabels = rows.map(row => row[0]);
    const rowBoldFlags = rows.map(row => row[2]);
    
    // Build the Excel data structure: Column A = labels, Columns B+ = period values
    const excelRows = [];
    
    // Row 1: Company name
    const companyName = firstData.partyName || firstData.title || 'Company';
    excelRows.push([companyName, ...Array(periodHeaders.length).fill('')]);
    
    // Row 2: Report title
    excelRows.push(['Profit & Loss', ...Array(periodHeaders.length).fill('')]);
    
    // Row 3: Period headers
    excelRows.push(['', ...periodHeaders]);
    
    // Rows 4+: Data rows
    for (let i = 3; i < rowLabels.length; i++) {
      const rowLabel = rowLabels[i];
      const rowValues = [];
      
      // Get value for each period
      for (const pnlData of dataArray) {
        const valueMap = this.buildPnLValueMap(pnlData);
        rowValues.push(valueMap.get(rowLabel) || '');
      }
      
      excelRows.push([rowLabel, ...rowValues]);
    }
    
    // Write all data to Excel
    const lastCol = this.getExcelColumn(1 + periodHeaders.length);
    const dataRange = worksheet.getRange(`A1:${lastCol}${excelRows.length}`);
    dataRange.values = excelRows;
    await context.sync();
    
    // Apply formatting
    await this.formatPnLMultiColumn(context, worksheet, rowBoldFlags, periodHeaders.length, numbersIn);
    
    // Set column widths
    worksheet.getRange("A:A").format.columnWidth = CONFIG.EXCEL.COLUMN_WIDTHS.DEFAULT_LABEL;
    for (let i = 0; i < periodHeaders.length; i++) {
      const colLetter = this.getExcelColumn(i + 2);
      worksheet.getRange(`${colLetter}:${colLetter}`).format.columnWidth = CONFIG.EXCEL.COLUMN_WIDTHS.DEFAULT_VALUE;
    }
    await context.sync();
  }

  /**
   * Apply formatting to multi-column P&L statement
   */
  async formatPnLMultiColumn(context, worksheet, rowBoldFlags, numPeriods, numbersIn = 'whole') {
    // Format header rows (1-3)
    const lastCol = this.getExcelColumn(1 + numPeriods);
    const headerRange = worksheet.getRange(`A1:${lastCol}3`);
    headerRange.format.font.bold = true;
    await context.sync();
    
    // Format data rows based on isBold flags
    for (let i = 0; i < rowBoldFlags.length; i++) {
      const rowIndex = i + 1;
      const isBold = rowBoldFlags[i];
      
      if (isBold) {
        const range = worksheet.getRange(`A${rowIndex}:${lastCol}${rowIndex}`);
        range.format.font.bold = true;
        await context.sync();
      }
    }
    
    // Format all number columns
    const numberFormat = this.getNumberFormat(numbersIn);
    for (let col = 2; col <= numPeriods + 1; col++) {
      const colLetter = this.getExcelColumn(col);
      const numberRange = worksheet.getRange(`${colLetter}4:${colLetter}${rowBoldFlags.length}`);
      numberRange.numberFormat = [[numberFormat]];
      await context.sync();
    }
    
    // Auto-fit columns
    worksheet.getRange(`A:${lastCol}`).format.autofitColumns();
    await context.sync();
  }

  /**
   * Build P&L statement rows from the new API structure
   */
  buildPnLRows(pnlData) {
    const rows = [];
    
    // Build header
    const partyName = pnlData.partyName || pnlData.title || 'Company';
    rows.push([partyName, '', true]);
    rows.push(['Profit & Loss', '', true]);
    rows.push([`Period: ${pnlData.period || `${pnlData.year}-${String(pnlData.month).padStart(2, '0')}`}`, '', true]);
    rows.push(['', '', false]);

    // Process each section
    if (pnlData.sections) {
      pnlData.sections.forEach((section, index) => {
        this.addSectionRows(rows, section, 0);
      });
    }

    return rows;
  }

  /**
   * Recursively add section rows with proper indentation
   */
  addSectionRows(rows, section, indentLevel) {
    // Add section header with total
    const indent = '  '.repeat(indentLevel);
    const sectionRow = {
      label: `${indent}${section.name}`,
      value: section.total || '',
      isBold: section.isBold || false,
      isCalculated: section.isCalculated || false,
      indentLevel: indentLevel
    };
    rows.push([sectionRow.label, sectionRow.value, sectionRow.isBold]);

    // Add children (line items)
    if (section.children && section.children.length > 0) {
      section.children.forEach(child => {
        const childIndent = '  '.repeat(indentLevel + 1);
        rows.push([
          `${childIndent}${child.name}`,
          child.amount || 0,
          false  // Children are never bold
        ]);
      });
    }

    // Add subsections recursively
    if (section.subSections && section.subSections.length > 0) {
      section.subSections.forEach(subSection => {
        this.addSectionRows(rows, subSection, indentLevel + 1);
      });
    }
  }

  /**
   * Apply formatting to P&L statement
   */
  async formatPnLStatement(context, worksheet, rows) {
    // Format all rows based on the isBold flag from API
    for (let i = 0; i < rows.length; i++) {
      const rowIndex = i + 1; // Excel is 1-indexed
      const isBold = rows[i][2]; // Third element is the isBold flag
      
      // Apply bold formatting if specified
      if (isBold) {
        const range = worksheet.getRange(`A${rowIndex}:B${rowIndex}`);
        range.format.font.bold = true;
        await context.sync();
      }
    }

    // Format numbers in column B (skip header rows)
    const numberRange = worksheet.getRange(`B4:B${rows.length}`);
    numberRange.numberFormat = [[CONFIG.EXCEL.NUMBER_FORMAT]];
    await context.sync();
  }

  /**
   * Get number format based on NumbersIn setting
   */
  getNumberFormat(numbersIn) {
    switch (numbersIn) {
      case 'thousands':
        return CONFIG.EXCEL.NUMBER_FORMAT_THOUSANDS;
      case 'millions':
        return CONFIG.EXCEL.NUMBER_FORMAT_MILLIONS;
      default:
        return CONFIG.EXCEL.NUMBER_FORMAT;
    }
  }

  /**
   * Write Balance Sheet Report data to Excel in formatted statement layout
   * Supports multiple periods in columns
   */
  async writeBalanceSheetToExcel(balanceSheetDataArray, sheetName, overwriteSheets, numbersIn = 'whole') {
    try {
      await Excel.run(async (context) => {
        const worksheet = await this.getOrCreateWorksheet(context, sheetName);
        
        // Convert single object to array for backward compatibility
        const dataArray = Array.isArray(balanceSheetDataArray) ? balanceSheetDataArray : [balanceSheetDataArray];
        
        if (dataArray.length === 0 || !dataArray[0].sections || dataArray[0].sections.length === 0) {
          worksheet.getRange("A1").values = [["No Balance Sheet data available"]];
          await context.sync();
          return;
        }

        // Check if we should overwrite or add/update columns
        if (overwriteSheets) {
          await this.clearWorksheet(context, worksheet, true);
          await this.writeBalanceSheetMultiColumn(context, worksheet, dataArray, true, numbersIn);
        } else {
          // Add or update specific period columns
          await this.addOrUpdateBalanceSheetColumns(context, worksheet, dataArray, numbersIn);
        }
      });
    } catch (error) {
      console.error(`Excel writing error for Balance Sheet ${sheetName}:`, error);
      throw new Error(`Failed to write Balance Sheet to Excel for ${sheetName}: ${error.message}`);
    }
  }

  /**
   * Write Balance Sheet data with multiple periods in columns
   */
  async writeBalanceSheetMultiColumn(context, worksheet, dataArray, isNewSheet, numbersIn = 'whole') {
    // Use the first dataset to build the row structure
    const firstData = dataArray[0];
    const rows = this.buildBalanceSheetRows(firstData);
    
    // Build headers for all periods
    const periodHeaders = dataArray.map(data => 
      data.period || `${data.year}-${String(data.month).padStart(2, '0')}`
    );
    
    // Extract row labels and metadata
    const rowLabels = rows.map(row => row[0]);
    const rowBoldFlags = rows.map(row => row[2]);
    
    // Build the Excel data structure: Column A = labels, Columns B+ = period values
    const excelRows = [];
    
    // Row 1: Company name
    const companyName = firstData.partyName || firstData.title || 'Company';
    excelRows.push([companyName, ...Array(periodHeaders.length).fill('')]);
    
    // Row 2: Report title
    excelRows.push(['Balance Sheet', ...Array(periodHeaders.length).fill('')]);
    
    // Row 3: Period headers
    excelRows.push(['', ...periodHeaders]);
    
    // Rows 4+: Data rows
    for (let i = 3; i < rowLabels.length; i++) {
      const rowLabel = rowLabels[i];
      const rowValues = [];
      
      // Get value for each period
      for (const balanceSheetData of dataArray) {
        const valueMap = this.buildBalanceSheetValueMap(balanceSheetData);
        rowValues.push(valueMap.get(rowLabel) || '');
      }
      
      excelRows.push([rowLabel, ...rowValues]);
    }
    
    // Write all data to Excel
    const lastCol = this.getExcelColumn(1 + periodHeaders.length);
    const dataRange = worksheet.getRange(`A1:${lastCol}${excelRows.length}`);
    dataRange.values = excelRows;
    await context.sync();
    
    // Apply formatting
    await this.formatBalanceSheetMultiColumn(context, worksheet, rowBoldFlags, periodHeaders.length, numbersIn);
    
    // Set column widths
    worksheet.getRange("A:A").format.columnWidth = CONFIG.EXCEL.COLUMN_WIDTHS.DEFAULT_LABEL;
    for (let i = 0; i < periodHeaders.length; i++) {
      const colLetter = this.getExcelColumn(i + 2);
      worksheet.getRange(`${colLetter}:${colLetter}`).format.columnWidth = CONFIG.EXCEL.COLUMN_WIDTHS.DEFAULT_VALUE;
    }
    await context.sync();
  }

  /**
   * Build Balance Sheet statement rows from the API structure
   */
  buildBalanceSheetRows(balanceSheetData) {
    const rows = [];
    
    // Build header
    const partyName = balanceSheetData.partyName || balanceSheetData.title || 'Company';
    rows.push([partyName, '', true]);
    rows.push(['Balance Sheet', '', true]);
    rows.push([`Period: ${balanceSheetData.period || `${balanceSheetData.year}-${String(balanceSheetData.month).padStart(2, '0')}`}`, '', true]);
    rows.push(['', '', false]);

    // Process each section
    if (balanceSheetData.sections) {
      balanceSheetData.sections.forEach((section, index) => {
        this.addSectionRows(rows, section, 0);
      });
    }

    return rows;
  }

  /**
   * Build a map of row labels to values from Balance Sheet data
   */
  buildBalanceSheetValueMap(balanceSheetData) {
    const valueMap = new Map();
    
    if (balanceSheetData.sections) {
      balanceSheetData.sections.forEach(section => {
        this.addSectionToValueMap(valueMap, section, 0);
      });
    }
    
    return valueMap;
  }

  /**
   * Apply formatting to multi-column Balance Sheet statement
   */
  async formatBalanceSheetMultiColumn(context, worksheet, rowBoldFlags, numPeriods, numbersIn = 'whole') {
    // Format header rows (1-3)
    const lastCol = this.getExcelColumn(1 + numPeriods);
    const headerRange = worksheet.getRange(`A1:${lastCol}3`);
    headerRange.format.font.bold = true;
    await context.sync();
    
    // Format data rows based on isBold flags
    for (let i = 0; i < rowBoldFlags.length; i++) {
      const rowIndex = i + 1;
      const isBold = rowBoldFlags[i];
      
      if (isBold) {
        const range = worksheet.getRange(`A${rowIndex}:${lastCol}${rowIndex}`);
        range.format.font.bold = true;
        await context.sync();
      }
    }
    
    // Format all number columns
    const numberFormat = this.getNumberFormat(numbersIn);
    for (let col = 2; col <= numPeriods + 1; col++) {
      const colLetter = this.getExcelColumn(col);
      const numberRange = worksheet.getRange(`${colLetter}4:${colLetter}${rowBoldFlags.length}`);
      numberRange.numberFormat = [[numberFormat]];
      await context.sync();
    }
    
    // Auto-fit columns
    worksheet.getRange(`A:${lastCol}`).format.autofitColumns();
    await context.sync();
  }

  /**
   * Add or update specific Balance Sheet columns without overwriting the entire sheet
   */
  async addOrUpdateBalanceSheetColumns(context, worksheet, dataArray, numbersIn = 'whole') {
    // Try to read existing headers to find where to place new columns
    let existingPeriods = [];
    let rowStructure = null;
    
    try {
      const usedRange = worksheet.getUsedRange();
      usedRange.load(["values", "rowCount", "columnCount"]);
      await context.sync();
      
      if (usedRange.values && usedRange.values.length > 0) {
        // Read existing period headers (row 3)
        if (usedRange.values.length >= 3) {
          existingPeriods = usedRange.values[2].slice(1).filter(p => p !== ""); // Skip column A
        }
        // Store row structure from column A
        rowStructure = usedRange.values.map(row => row[0]);
      }
    } catch {
      // Sheet is empty, write fresh
      await this.writeBalanceSheetMultiColumn(context, worksheet, dataArray, true, numbersIn);
      return;
    }

    // For each new period, add or update its column
    for (const balanceSheetData of dataArray) {
      const periodLabel = balanceSheetData.period || `${balanceSheetData.year}-${String(balanceSheetData.month).padStart(2, '0')}`;
      const existingIndex = existingPeriods.indexOf(periodLabel);
      
      if (existingIndex >= 0) {
        // Update existing column (existingIndex + 1 because column A is labels)
        const colLetter = this.getExcelColumn(existingIndex + 2);
        await this.updateBalanceSheetColumn(context, worksheet, balanceSheetData, colLetter, rowStructure, numbersIn);
      } else {
        // Add new column at the end
        const newColIndex = existingPeriods.length + 2; // +2 because column A is labels, and we're 1-indexed
        const colLetter = this.getExcelColumn(newColIndex);
        existingPeriods.push(periodLabel);
        await this.addBalanceSheetColumn(context, worksheet, balanceSheetData, colLetter, rowStructure, numbersIn);
      }
    }
  }

  /**
   * Update a specific Balance Sheet column
   */
  async updateBalanceSheetColumn(context, worksheet, balanceSheetData, colLetter, rowStructure, numbersIn = 'whole') {
    const periodLabel = balanceSheetData.period || `${balanceSheetData.year}-${String(balanceSheetData.month).padStart(2, '0')}`;
    
    // Update header
    worksheet.getRange(`${colLetter}3`).values = [[periodLabel]];
    await context.sync();
    
    // Build value map from balanceSheetData
    const valueMap = this.buildBalanceSheetValueMap(balanceSheetData);
    
    // Update values starting from row 4
    for (let i = 3; i < rowStructure.length; i++) {
      const rowLabel = rowStructure[i];
      const value = valueMap.get(rowLabel) || '';
      worksheet.getRange(`${colLetter}${i + 1}`).values = [[value]];
    }
    await context.sync();
    
    // Apply number formatting
    const numberRange = worksheet.getRange(`${colLetter}4:${colLetter}${rowStructure.length}`);
    numberRange.numberFormat = [[this.getNumberFormat(numbersIn)]];
    await context.sync();
  }

  /**
   * Add a new Balance Sheet column
   */
  async addBalanceSheetColumn(context, worksheet, balanceSheetData, colLetter, rowStructure, numbersIn = 'whole') {
    const periodLabel = balanceSheetData.period || `${balanceSheetData.year}-${String(balanceSheetData.month).padStart(2, '0')}`;
    
    // Add header
    worksheet.getRange(`${colLetter}3`).values = [[periodLabel]];
    await context.sync();
    
    // Build value map from balanceSheetData
    const valueMap = this.buildBalanceSheetValueMap(balanceSheetData);
    
    // Add values starting from row 4
    for (let i = 3; i < rowStructure.length; i++) {
      const rowLabel = rowStructure[i];
      const value = valueMap.get(rowLabel) || '';
      worksheet.getRange(`${colLetter}${i + 1}`).values = [[value]];
    }
    await context.sync();
    
    // Apply number formatting
    const numberRange = worksheet.getRange(`${colLetter}4:${colLetter}${rowStructure.length}`);
    numberRange.numberFormat = [[this.getNumberFormat(numbersIn)]];
    await context.sync();
    
    // Set column width
    worksheet.getRange(`${colLetter}:${colLetter}`).format.columnWidth = CONFIG.EXCEL.COLUMN_WIDTHS.DEFAULT_VALUE;
    await context.sync();
  }

  /**
   * Write hierarchical report (P&L or Balance Sheet) to Excel matching Blazor report format
   * Displays sections with monthly columns and expandable account details
   * @param {Object} reportData - Hierarchical report data from v2 API (with monthlyReports)
   * @param {string} sheetName - Name of the sheet
   * @param {boolean} overwriteSheets - Whether to overwrite existing data
   * @param {string} numbersIn - Numbers format (whole, thousands, millions)
   * @param {Array<number>} monthsToDisplay - Array of month numbers (1-12) to display
   * @param {string} reportType - 'pnl' or 'balance-sheet'
   */
  async writeHierarchicalReportToExcel(reportData, sheetName, overwriteSheets, numbersIn = 'whole', monthsToDisplay = null, reportType = 'pnl') {
    console.log('=== Starting writeHierarchicalReportToExcel ===');
    console.log('Sheet name:', sheetName);
    console.log('Report type:', reportType);
    console.log('Report data keys:', Object.keys(reportData || {}));
    
    try {
      await Excel.run(async (context) => {
        console.log('Excel.run started, getting worksheet...');
        let worksheet;
        try {
          worksheet = await this.getOrCreateWorksheet(context, sheetName);
          console.log('Worksheet obtained, clearing if needed...');
          await this.clearWorksheet(context, worksheet, overwriteSheets);
          console.log('Worksheet cleared');
        } catch (worksheetError) {
          console.error('Error in worksheet setup:', worksheetError);
          throw new Error(`Failed to setup worksheet: ${worksheetError.message}`);
        }

        // Handle v2 response format with monthlyReports
        const consolidatedReport = reportData.consolidatedReport || reportData;
        const monthlyReports = reportData.monthlyReports || {};
        const reportMonths = reportData.months || monthsToDisplay || [];

        console.log('Consolidated report sections count:', consolidatedReport?.sections?.length || 0);
        console.log('Monthly reports keys:', Object.keys(monthlyReports));
        console.log('Report months:', reportMonths);
        
        // Debug: Check if monthlyReports has actual data
        if (Object.keys(monthlyReports).length > 0) {
          const firstMonthKey = Object.keys(monthlyReports)[0];
          const firstMonthReport = monthlyReports[firstMonthKey];
          console.log('First month report sample:', {
            month: firstMonthKey,
            title: firstMonthReport?.title,
            sectionsCount: firstMonthReport?.sections?.length || 0,
            firstSection: firstMonthReport?.sections?.[0]
          });
          
          // Check a specific section with data (like Retained Earnings)
          if (firstMonthReport?.sections) {
            const retainedEarnings = firstMonthReport.sections.find(s => s.property === 'RetainedEarnings');
            console.log('Retained Earnings in first month:', retainedEarnings);
            
            // Check all sections to see if any have non-zero values
            const sectionsWithData = firstMonthReport.sections.filter(s => s.total !== 0 && s.total !== null);
            console.log(`Sections with non-zero data in month ${firstMonthKey}:`, sectionsWithData.length);
            if (sectionsWithData.length > 0) {
              console.log('Sample section with data:', sectionsWithData[0]);
            }
          }
        } else {
          console.warn('No monthly reports found!');
        }

        // Validate data structure
        if (!consolidatedReport) {
          throw new Error('Invalid response: consolidatedReport is missing');
        }
        if (!consolidatedReport.sections || !Array.isArray(consolidatedReport.sections)) {
          throw new Error('Invalid response: consolidatedReport.sections is missing or not an array');
        }

        if (!consolidatedReport || !consolidatedReport.sections || consolidatedReport.sections.length === 0) {
          console.log('No sections found, writing "No data available"');
          worksheet.getRange("A1").values = [["No data available"]];
          await context.sync();
          return;
        }

        // Determine which months to display
        const displayMonths = (reportMonths.length > 0) 
          ? reportMonths.sort((a, b) => a - b)
          : (monthsToDisplay && monthsToDisplay.length > 0 
            ? monthsToDisplay.sort((a, b) => a - b)
            : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
        
        const monthNames = CONFIG.DATE.MONTH_NAMES;
        const displayMonthNames = displayMonths.map(monthNum => monthNames[monthNum - 1]);
        // Balance sheet should never show Total column, only P&L shows it when multiple months
        const showTotal = reportType === 'pnl' && displayMonths.length > 1;

        // Build headers
        const headers = ['Account Name', ...displayMonthNames];
        if (showTotal) {
          headers.push('Total');
        }

        // Start writing data
        let currentRow = 1;

        // Title row
        try {
          const title = consolidatedReport.title || (reportType === 'pnl' ? 'Profit & Loss' : 'Balance Sheet');
          worksheet.getRange(`A${currentRow}`).values = [[String(title)]];
          worksheet.getRange(`A${currentRow}`).format.font.bold = true;
          worksheet.getRange(`A${currentRow}`).format.font.fontSize = 14;
          await context.sync();
        } catch (titleError) {
          console.error('Error writing title:', titleError);
          throw titleError;
        }
        currentRow++;

        // Metadata rows
        try {
          if (consolidatedReport.partyName) {
            worksheet.getRange(`A${currentRow}`).values = [[`Company: ${String(consolidatedReport.partyName)}`]];
            await context.sync();
            currentRow++;
          }
          const periodText = `Period: ${String(consolidatedReport.period || consolidatedReport.year || '')}`;
          worksheet.getRange(`A${currentRow}`).values = [[periodText]];
          await context.sync();
          currentRow++;
          currentRow++; // Empty row
        } catch (metadataError) {
          console.error('Error writing metadata:', metadataError);
          throw metadataError;
        }

        // Header row
        const headerRow = currentRow;
        const lastHeaderCol = this.getExcelColumn(headers.length);
        // Clean headers - replace null/undefined with empty string
        const cleanHeaders = headers.map(h => {
          if (h === null || h === undefined) return '';
          return String(h);
        });
        
        console.log(`Writing headers at row ${headerRow}, columns A to ${lastHeaderCol}, ${cleanHeaders.length} headers`);
        console.log('Headers:', cleanHeaders);
        
        try {
          const headerRangeStr = `A${headerRow}:${lastHeaderCol}${headerRow}`;
          console.log('Header range string:', headerRangeStr);
          console.log('Headers array:', cleanHeaders);
          console.log('Headers length:', cleanHeaders.length);
          console.log('Last header column:', lastHeaderCol, 'Column number:', headers.length);
          
          // Validate range format
          if (!/^[A-Z]+\d+:[A-Z]+\d+$/.test(headerRangeStr)) {
            throw new Error(`Invalid header range format: ${headerRangeStr}`);
          }
          
          const headerRange = worksheet.getRange(headerRangeStr);
          
          // Write values first
          headerRange.values = [cleanHeaders];
          await context.sync();
          console.log('Header values written');
          
          // Apply formatting
          headerRange.format.font.bold = true;
          headerRange.format.fill.color = CONFIG.EXCEL.HEADER_COLOR;
          headerRange.format.font.color = CONFIG.EXCEL.HEADER_FONT_COLOR;
          await context.sync();
          console.log('Headers written successfully');
        } catch (headerError) {
          console.error('Error writing headers:', headerError);
          console.error('Header range:', `A${headerRow}:${lastHeaderCol}${headerRow}`);
          console.error('Headers array:', cleanHeaders);
          console.error('Headers length:', cleanHeaders.length, 'Expected:', headers.length);
          console.error('Last header column:', lastHeaderCol, 'Column number:', headers.length);
          throw new Error(`Failed to write headers: ${headerError.message}`);
        }
        currentRow++;

        // Process sections - match Blazor GetDisplayRows() structure exactly
        // In Blazor: yield section, then if not calculated and expanded, yield children
        // In Excel: always show sections and their children (no collapse/expand)
        const allRows = [];
        
        for (const section of consolidatedReport.sections) {
          if (!section) continue; // Skip null/undefined sections
          
          // Always yield the section row first (matching Blazor)
          if (section.isCalculated) {
            // Calculated row (Total Assets, Total Equity, etc.) - NO children, NO expand/collapse
            const row = this.buildCalculatedSectionRow(section, displayMonths, monthlyReports, showTotal);
            // Ensure row length matches headers length and all values are valid
            while (row.length < headers.length) {
              row.push('');
            }
            if (row.length > headers.length) {
              row.length = headers.length;
            }
            // Replace null/undefined with empty string, ensure numbers are numbers
            const cleanRow = row.map((val, idx) => {
              if (val === null || val === undefined) return '';
              if (idx === 0) return String(val);
              const num = typeof val === 'number' ? val : (typeof val === 'string' && val !== '' ? parseFloat(val) : 0);
              return isNaN(num) ? 0 : num;
            });
            
            allRows.push({ 
              row: cleanRow, 
              isBold: section.isBold, 
              isItalic: !section.isBold, 
              isCalculated: true,
              isSection: false
            });
          } else {
            // Data section (Current Assets, Equity, etc.) - HAS children, always show them in Excel
            const sectionRow = this.buildDataSectionRow(section, displayMonths, monthlyReports, showTotal);
            // Ensure row length matches headers length and all values are valid
            while (sectionRow.length < headers.length) {
              sectionRow.push('');
            }
            if (sectionRow.length > headers.length) {
              sectionRow.length = headers.length;
            }
            // Replace null/undefined with empty string, ensure numbers are numbers
            const cleanSectionRow = sectionRow.map((val, idx) => {
              if (val === null || val === undefined) return '';
              if (idx === 0) return String(val);
              const num = typeof val === 'number' ? val : (typeof val === 'string' && val !== '' ? parseFloat(val) : 0);
              return isNaN(num) ? 0 : num;
            });
            
            // Add the section row (bold, with background color)
            allRows.push({ 
              row: cleanSectionRow, 
              isBold: true, 
              isSection: true,
              isCalculated: false
            });
            
            // Show child accounts immediately after section (always expanded in Excel, matching Blazor when expanded)
            if (section.children && Array.isArray(section.children) && section.children.length > 0) {
              for (const child of section.children) {
                if (!child) continue; // Skip null/undefined children
                
                const childRow = this.buildChildAccountRow(child, displayMonths, monthlyReports, section, showTotal);
                // Ensure row length matches headers length and all values are valid
                while (childRow.length < headers.length) {
                  childRow.push('');
                }
                if (childRow.length > headers.length) {
                  childRow.length = headers.length;
                }
                // Replace null/undefined with empty string, ensure numbers are numbers
                const cleanChildRow = childRow.map((val, idx) => {
                  if (val === null || val === undefined) return '';
                  if (idx === 0) return String(val);
                  const num = typeof val === 'number' ? val : (typeof val === 'string' && val !== '' ? parseFloat(val) : 0);
                  return isNaN(num) ? 0 : num;
                });
                
                // Add child row (not bold, indented)
                allRows.push({ 
                  row: cleanChildRow, 
                  isBold: false, 
                  isChild: true,
                  isSection: false,
                  isCalculated: false
                });
              }
            }
          }
        }
        
        // Write all rows at once using a single range - more reliable
        if (allRows.length > 0) {
          const lastCol = this.getExcelColumn(headers.length);
          const startRow = currentRow;
          const endRow = currentRow + allRows.length - 1;
          
          console.log(`Preparing to write ${allRows.length} rows (${startRow} to ${endRow}), ${headers.length} columns (A to ${lastCol})`);
          
          // Prepare all row data with proper validation
          const allRowValues = [];
          for (let i = 0; i < allRows.length; i++) {
            const rowInfo = allRows[i];
            
            // Ensure row has correct length
            let row = rowInfo.row;
            if (row.length !== headers.length) {
              console.warn(`Row ${i} length mismatch: ${row.length} vs ${headers.length}, fixing...`);
              row = [...row]; // Copy array
              while (row.length < headers.length) {
                row.push('');
              }
              row = row.slice(0, headers.length);
            }
            
            // Validate and convert all values
            const validatedRow = row.map((val, colIdx) => {
              if (colIdx === 0) {
                // First column is text
                return val === null || val === undefined ? '' : String(val);
              } else {
                // Other columns are numbers
                if (val === null || val === undefined || val === '') {
                  return 0;
                }
                if (typeof val === 'number') {
                  return val;
                }
                if (typeof val === 'string') {
                  const num = parseFloat(val);
                  return isNaN(num) ? 0 : num;
                }
                return 0;
              }
            });
            
            // Double-check length
            if (validatedRow.length !== headers.length) {
              console.error(`Row ${i} validation failed: length ${validatedRow.length} vs ${headers.length}`);
              // Force correct length
              while (validatedRow.length < headers.length) {
                validatedRow.push(validatedRow.length === 0 ? '' : 0);
              }
              validatedRow.length = headers.length;
            }
            
            allRowValues.push(validatedRow);
          }
          
          // Verify all rows have correct length
          const invalidRows = allRowValues.filter(r => r.length !== headers.length);
          if (invalidRows.length > 0) {
            console.error(`Found ${invalidRows.length} rows with incorrect length`);
            throw new Error(`Data validation failed: ${invalidRows.length} rows have incorrect length`);
          }
          
          try {
            // Write all data at once
            const rangeStr = `A${startRow}:${lastCol}${endRow}`;
            console.log(`Writing range: ${rangeStr} with ${allRowValues.length} rows x ${headers.length} columns`);
            console.log('Sample first row:', allRowValues[0]);
            console.log('Sample last row:', allRowValues[allRowValues.length - 1]);
            
            // Validate the range string format
            if (!/^[A-Z]+\d+:[A-Z]+\d+$/.test(rangeStr)) {
              throw new Error(`Invalid range format: ${rangeStr}`);
            }
            
            const dataRange = worksheet.getRange(rangeStr);
            
            // Ensure values is a proper 2D array
            if (!Array.isArray(allRowValues) || allRowValues.length === 0) {
              throw new Error('allRowValues must be a non-empty array');
            }
            
            // Validate each row is an array
            for (let i = 0; i < allRowValues.length; i++) {
              if (!Array.isArray(allRowValues[i])) {
                throw new Error(`Row ${i} is not an array: ${typeof allRowValues[i]}`);
              }
              if (allRowValues[i].length !== headers.length) {
                throw new Error(`Row ${i} length mismatch: ${allRowValues[i].length} vs ${headers.length}`);
              }
            }
            
            // Write row by row to avoid Excel API issues with large arrays
            // This is more reliable than writing all at once
            for (let i = 0; i < allRowValues.length; i++) {
              const rowNum = startRow + i;
              const rowRange = worksheet.getRange(`A${rowNum}:${lastCol}${rowNum}`);
              rowRange.values = [allRowValues[i]];
            }
            await context.sync();
            
            console.log('Data written successfully, applying formatting...');
            
            // Apply formatting row by row - match Blazor styling exactly
            // Format in smaller batches to avoid API overload
            const batchSize = 5;
            for (let batchStart = 0; batchStart < allRows.length; batchStart += batchSize) {
              const batchEnd = Math.min(batchStart + batchSize, allRows.length);
              
              for (let i = batchStart; i < batchEnd; i++) {
                const rowInfo = allRows[i];
                const rowNum = startRow + i;
                
                // Validate row number is valid
                if (rowNum < 1 || rowNum > 1048576) {
                  console.warn(`Invalid row number ${rowNum}, skipping`);
                  continue;
                }
                
                try {
                  const rangeStr = `A${rowNum}:${lastCol}${rowNum}`;
                  
                  // Validate range string format
                  if (!/^[A-Z]+\d+:[A-Z]+\d+$/.test(rangeStr)) {
                    console.warn(`Invalid range format: ${rangeStr}, skipping`);
                    continue;
                  }
                  
                  const rowRange = worksheet.getRange(rangeStr);
                  
                  // Match Blazor row classes:
                  // - section-row: data sections (Current Assets, Equity, etc.) - bold, light blue background
                  // - subtotal-row: calculated rows (Total Assets, etc.) - italic if not bold
                  // - subtotal-row-bold: calculated rows that are bold (Total Assets, Total Equity, etc.) - bold, yellow background
                  // - child-row: child accounts - no special formatting, just indented text
                  
                  if (rowInfo.isSection) {
                    // Data section row - bold, light blue background (matching Blazor section-row)
                    rowRange.format.font.bold = true;
                    rowRange.format.fill.color = '#D9E1F2'; // Light blue
                  } else if (rowInfo.isCalculated) {
                    // Calculated row
                    if (rowInfo.isBold) {
                      // Bold calculated row (Total Assets, Total Equity, etc.) - yellow background
                      rowRange.format.font.bold = true;
                      rowRange.format.fill.color = '#FFFF99'; // Light yellow
                    } else {
                      // Non-bold calculated row (Total Intangible Assets, etc.) - italic
                      worksheet.getRange(`A${rowNum}`).format.font.italic = true;
                    }
                  }
                  // Child rows need no special formatting - just plain text (indentation is in the text itself)
                } catch (formatError) {
                  console.warn(`Error formatting row ${rowNum}:`, formatError);
                  // Continue with next row
                }
              }
              
              // Sync after each batch
              try {
                await context.sync();
              } catch (syncError) {
                console.warn(`Error syncing batch ${batchStart}-${batchEnd}:`, syncError);
                // Continue with next batch
              }
            }
            
            console.log('Formatting applied successfully');
            
            currentRow += allRows.length;
          } catch (writeError) {
            console.error('Error writing data range:', writeError);
            console.error('Range:', `A${startRow}:${lastCol}${endRow}`);
            console.error('Rows:', allRowValues.length, 'Columns:', headers.length);
            console.error('First row sample:', allRowValues[0]);
            console.error('Last row sample:', allRowValues[allRowValues.length - 1]);
            console.error('All row lengths:', allRowValues.map((r, i) => `Row ${i}: ${r.length}`));
            throw new Error(`Failed to write data to Excel: ${writeError.message}`);
          }
        }

        // Format number columns (only if we have data rows)
        if (currentRow > headerRow + 1) {
          const numberFormat = this.getNumberFormat(numbersIn);
          for (let col = 2; col <= headers.length; col++) {
            const colLetter = this.getExcelColumn(col);
            try {
              const numberRange = worksheet.getRange(`${colLetter}${headerRow + 1}:${colLetter}${currentRow - 1}`);
              // Use 2D array format for numberFormat (matching other methods)
              numberRange.numberFormat = [[numberFormat]];
              numberRange.format.alignment.horizontal = 'Right';
            } catch (formatError) {
              console.warn(`Failed to format column ${colLetter}:`, formatError);
              // Continue with other columns
            }
          }
        }

        // Set column widths - use autofit for number columns to prevent "#" symbols
        worksheet.getRange("A:A").format.columnWidth = 50;
        try {
          // Autofit number columns to prevent "#" symbols when numbers are too wide
          const numberColumnsRange = worksheet.getRange(`${this.getExcelColumn(2)}:${this.getExcelColumn(headers.length)}`);
          numberColumnsRange.format.autofitColumns();
        } catch (autofitError) {
          // If autofit fails, use default width
          console.warn('Autofit failed, using default column widths:', autofitError);
          for (let col = 2; col <= headers.length; col++) {
            const colLetter = this.getExcelColumn(col);
            worksheet.getRange(`${colLetter}:${colLetter}`).format.columnWidth = 18; // Increased from 15 to 18
          }
        }

        await context.sync();
      });
    } catch (error) {
      console.error(`Excel writing error for hierarchical report ${sheetName}:`, error);
      throw new Error(`Failed to write hierarchical report to Excel: ${error.message}`);
    }
  }

  /**
   * Build a row for a calculated section with monthly values from monthlyReports
   */
  buildCalculatedSectionRow(section, displayMonths, monthlyReports, showTotal) {
    const row = [section.name || ''];
    
    // Get monthly amounts from monthlyReports (matching Blazor GetMonthAmount logic)
    // Handle both string and numeric keys
    for (const month of displayMonths) {
      const monthKey = String(month);
      const monthReport = monthlyReports[monthKey] || monthlyReports[month];
      
      if (monthReport && monthReport.sections && Array.isArray(monthReport.sections)) {
        const monthSection = monthReport.sections.find(s => s.property === section.property);
        if (monthSection) {
          const value = monthSection.total;
          // Ensure we're getting the actual value, not defaulting to 0
          row.push(typeof value === 'number' ? value : (value === null || value === undefined ? 0 : parseFloat(value) || 0));
        } else {
          // Section not found in monthly report - use 0
          console.warn(`Section ${section.property} not found in month ${month} report`);
          row.push(0);
        }
      } else {
        // Monthly report not found - use 0
        console.warn(`Month ${month} report not found or invalid`);
        row.push(0);
      }
    }
    
    if (showTotal) {
      // Use consolidated report total
      const totalValue = section.total;
      row.push(typeof totalValue === 'number' ? totalValue : (totalValue === null || totalValue === undefined ? 0 : parseFloat(totalValue) || 0));
    }
    
    return row;
  }

  /**
   * Build a row for a data section with monthly values
   */
  buildDataSectionRow(section, displayMonths, monthlyReports, showTotal) {
    const row = [section.name || ''];
    
    // Get monthly amounts from monthlyReports (matching Blazor GetMonthAmount logic)
    // Handle both string and numeric keys
    for (const month of displayMonths) {
      const monthKey = String(month);
      const monthReport = monthlyReports[monthKey] || monthlyReports[month];
      
      if (monthReport && monthReport.sections && Array.isArray(monthReport.sections)) {
        const monthSection = monthReport.sections.find(s => s.property === section.property);
        if (monthSection) {
          const value = monthSection.total;
          // Ensure we're getting the actual value, not defaulting to 0
          row.push(typeof value === 'number' ? value : (value === null || value === undefined ? 0 : parseFloat(value) || 0));
        } else {
          // Section not found in monthly report - use 0
          row.push(0);
        }
      } else {
        // Monthly report not found - use 0
        row.push(0);
      }
    }
    
    if (showTotal) {
      // Use consolidated report total
      const totalValue = section.total;
      row.push(typeof totalValue === 'number' ? totalValue : (totalValue === null || totalValue === undefined ? 0 : parseFloat(totalValue) || 0));
    }
    
    return row;
  }

  /**
   * Build a row for a child account with monthly values
   */
  buildChildAccountRow(child, displayMonths, monthlyReports, parentSection, showTotal) {
    const childName = child.name || '';
    const accountNumber = child.accountNumber || '';
    // Match Blazor: ms-4 class = margin-start-4, so use 4 spaces for indentation
    // Format: "    Account Name (AccountNumber)" - exactly matching Blazor display
    const accountDisplay = accountNumber 
      ? `    ${childName} (${accountNumber})`
      : `    ${childName}`;
    const row = [accountDisplay];
    
    // Get monthly amounts from monthlyReports (matching Blazor GetMonthAmountForAccount logic)
    // Handle both string and numeric keys
    for (const month of displayMonths) {
      const monthKey = String(month);
      const monthReport = monthlyReports[monthKey] || monthlyReports[month];
      
      if (monthReport && monthReport.sections && Array.isArray(monthReport.sections)) {
        const monthSection = monthReport.sections.find(s => s.property === parentSection.property);
        if (monthSection && monthSection.children && Array.isArray(monthSection.children)) {
          const monthAccount = monthSection.children.find(a => a.accountNumber === child.accountNumber);
          if (monthAccount) {
            const value = monthAccount.amount;
            // Ensure we're getting the actual value
            row.push(typeof value === 'number' ? value : (value === null || value === undefined ? 0 : parseFloat(value) || 0));
          } else {
            row.push(0);
          }
        } else {
          row.push(0);
        }
      } else {
        row.push(0);
      }
    }
    
    if (showTotal) {
      // Use child's total from consolidated report
      const totalValue = child.amount;
      row.push(typeof totalValue === 'number' ? totalValue : (totalValue === null || totalValue === undefined ? 0 : parseFloat(totalValue) || 0));
    }
    
    return row;
  }

  /**
   * Write a flat, pivot-ready trial balance: one row per company × account × month.
   * Creates an Excel Table so users can add PivotTables and Slicers (Party, Month, Account, etc.).
   */
  async writePivotReadyTrialBalanceSheet(rows, meta, overwriteSheets = true) {
    const sheetName = CONFIG.SHEETS.PIVOT_TB_SHEET;
    const tableName = CONFIG.EXCEL.PIVOT_TB_TABLE_NAME;
    const headers = [
      'PartyId',
      'PartyName',
      'AccountNumber',
      'AccountName',
      'AccountClass',
      'Year',
      'Month',
      'MonthName',
      'Amount',
      'EliminationsApplied',
      'DataSource'
    ];

    await Excel.run(async (context) => {
      const worksheet = await this.getOrCreateWorksheet(context, sheetName);
      await this.clearWorksheet(context, worksheet, overwriteSheets);

      const title = `Pivot-ready trial balance — ${meta.year} — ${meta.periodLabel || ''}`;
      worksheet.getRange('A1').values = [[title]];
      worksheet.getRange('A1').format.font.bold = true;
      worksheet.getRange('A1').format.font.size = 12;
      worksheet.getRange('A2').values = [[
        'Insert → PivotTable → From Table/Range → select table "' +
          tableName +
          '". Then add Slicers (Insert → Slicer) on PartyName, MonthName, AccountClass, AccountNumber.'
      ]];
      worksheet.getRange('A2').format.wrapText = true;
      worksheet.getRange('3:3').format.rowHeight = 8;

      const headerRow = 4;
      if (!rows || rows.length === 0) {
        worksheet.getRange(`A${headerRow}`).values = [['No data — check API and company selection']];
        await context.sync();
        return;
      }

      const lastCol = this.getExcelColumn(headers.length);
      const headerRange = worksheet.getRange(`A${headerRow}:${lastCol}${headerRow}`);
      headerRange.values = [headers];
      headerRange.format.font.bold = true;
      headerRange.format.fill.color = CONFIG.EXCEL.HEADER_COLOR;
      headerRange.format.font.color = CONFIG.EXCEL.HEADER_FONT_COLOR;

      const dataStart = headerRow + 1;
      const lastRow = headerRow + rows.length;
      const body = rows.map((r) => [
        r.partyId,
        r.partyName,
        r.accountNumber,
        r.accountName,
        r.accountClass,
        r.year,
        r.month,
        r.monthName,
        r.amount,
        r.eliminationsApplied,
        r.dataSource
      ]);
      worksheet.getRange(`A${dataStart}:${lastCol}${lastRow}`).values = body;

      const numFmt = CONFIG.EXCEL.NUMBER_FORMAT;
      worksheet.getRange(`I${dataStart}:I${lastRow}`).numberFormat = [[numFmt]];
      worksheet.getRange(`I${dataStart}:I${lastRow}`).format.alignment.horizontal = 'Right';

      const tableRange = worksheet.getRange(`A${headerRow}:${lastCol}${lastRow}`);
      const table = worksheet.tables.add(tableRange, true);
      table.name = tableName;
      table.style = 'TableStyleMedium2';

      worksheet.getRange('A:A').format.columnWidth = 10;
      worksheet.getRange('B:B').format.columnWidth = 28;
      worksheet.getRange('C:C').format.columnWidth = 14;
      worksheet.getRange('D:D').format.columnWidth = 36;
      worksheet.getRange('E:E').format.columnWidth = 18;
      worksheet.getRange('F:H').format.columnWidth = 10;
      worksheet.getRange('I:I').format.columnWidth = 16;

      await context.sync();
    });
  }

  // ─── Raw Sync Infrastructure ─────────────────────────────────────────────

  /**
   * Safely delete a workbook-level named range (no-op if it doesn't exist).
   * Must be called inside an active Excel.run context.
   */
  _deleteNamedRange(context, name) {
    const item = context.workbook.names.getItemOrNullObject(name);
    item.load('isNullObject');
    return context.sync().then(() => {
      if (!item.isNullObject) item.delete();
    }).catch(() => { /* ignore */ });
  }

  /**
   * Write data into a Raw_* sheet as a properly-structured Excel Table.
   * Fully rebuilds the sheet each time so row counts never mismatch.
   * Writes in chunks of 500 rows to stay well within Office.js limits.
   *
   * @param {string}   sheetName  worksheet name  (e.g. 'Raw_Parties')
   * @param {string}   tableName  table name       (e.g. 'tbl_RawParties')
   * @param {string[]} headers    column headers
   * @param {any[][]}  rows       2-D data array — must match headers.length per row
   */
  async replaceTableData(sheetName, tableName, headers, rows) {
    const CHUNK_SIZE = 500;
    const colCount = headers.length;
    const lastCol = this.getExcelColumn(colCount);

    // Sanitise every cell: null/undefined → '' so Excel never sees undefined
    const safeRows = rows.map(row =>
      Array.from({ length: colCount }, (_, i) => {
        const v = Array.isArray(row) ? row[i] : undefined;
        return (v === null || v === undefined) ? '' : v;
      })
    );

    await Excel.run(async (context) => {
      // ── 1. Get or create the sheet ──────────────────────────────────────
      let sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
      sheet.load('isNullObject');
      await context.sync();
      if (sheet.isNullObject) {
        sheet = context.workbook.worksheets.add(sheetName);
        await context.sync();
      }

      // ── 2. Safe-clear existing content ──────────────────────────────────
      const usedRange = sheet.getUsedRangeOrNullObject();
      usedRange.load('isNullObject');
      await context.sync();
      if (!usedRange.isNullObject) {
        // Delete the existing table first (required before clearing its range)
        const existingTable = sheet.tables.getItemOrNullObject(tableName);
        existingTable.load('isNullObject');
        await context.sync();
        if (!existingTable.isNullObject) {
          existingTable.delete();
          await context.sync();
        }
        sheet.getRange('A:XFD').clear(Excel.ClearApplyTo.all);
        await context.sync();
      }

      // ── 3. Write header row ─────────────────────────────────────────────
      const headerRange = sheet.getRange(`A1:${lastCol}1`);
      headerRange.values = [headers];
      headerRange.format.font.bold = true;
      headerRange.format.fill.color = '#1F6091';
      headerRange.format.font.color = '#FFFFFF';
      await context.sync();

      // ── 4. Write data in chunks ─────────────────────────────────────────
      for (let start = 0; start < safeRows.length; start += CHUNK_SIZE) {
        const chunk = safeRows.slice(start, start + CHUNK_SIZE);
        const startRow = 2 + start;
        const endRow = startRow + chunk.length - 1;
        sheet.getRange(`A${startRow}:${lastCol}${endRow}`).values = chunk;
        await context.sync();
      }

      // ── 5. Create Excel Table over written range ────────────────────────
      const totalDataRows = safeRows.length;
      const tableLastRow = totalDataRows > 0 ? totalDataRows + 1 : 2;
      // If no data rows, create a table with just the header (needs at least 1 data row placeholder)
      if (totalDataRows === 0) {
        // Write one blank placeholder row so the table has a valid body
        sheet.getRange(`A2:${lastCol}2`).values = [Array(colCount).fill('')];
        await context.sync();
      }
      const tableRange = sheet.getRange(`A1:${lastCol}${tableLastRow}`);
      const newTable = sheet.tables.add(tableRange, true);
      newTable.name = tableName;
      newTable.style = 'TableStyleLight2';
      await context.sync();

      // Remove the placeholder blank row if we added one
      if (totalDataRows === 0) {
        const body = newTable.getDataBodyRange();
        body.delete(Excel.DeleteShiftDirection.up);
        await context.sync();
      }

      // Auto-fit columns (best-effort)
      try {
        sheet.getUsedRange().format.autofitColumns();
        await context.sync();
      } catch { /* non-critical */ }
    });
  }

  /**
   * Build (or fully rebuild) the _Config interactive filter panel sheet.
   *
   * Clean 4-pair-column form layout — each section has a label row above its value cell:
   *   Col A (label) + B (value)  = COMPANY section
   *   Col C (label) + D (value)  = PERIOD section
   *   Col E (label) + F (value)  = REPORT SETTINGS section
   *   Col G (label) + H (value)  = OPTIONS section
   *   Party dropdown list lives on hidden sheet PARTY_PICK_SHEET (not on _Config).
   *
   *   Row 1 : Title banner (A1:H1)
   *   Row 2 : empty
   *   Row 3 : Section headers (A3:B3 | C3:D3 | E3:F3 | G3:H3)
   *   Row 4 : Labels     — Party IDs | Year | Report Currency | Show Adjusted
   *   Row 5 : Values     — [cfg_SelectedCompanies] | [cfg_Year] | [cfg_ReportCurrency] | [cfg_ShowAdjusted]
   *   Row 6 : Labels     — Group Entity | Frequency | Numbers In | Include Historical
   *   Row 7 : Values     — [cfg_GroupEntity] | [cfg_Frequency] | [cfg_NumbersIn] | [cfg_IncludeHistorical]
   *   Row 8 : Labels     — Sync Scope | Period | Profile ID | Last Sync
   *   Row 9 : Values     — [cfg_SyncScope] | [cfg_Period] | [cfg_ProfileId] | [cfg_LastSync]
   *   Row 10: hint banner
   *   Row 12: HOW TO USE header + instructions
   *
   * @param {number}   defaultYear
   * @param {Array}    partyNames      [{partyId, name}] for lookup table
   * @param {string[]} currencyCodes   ISO codes for currency dropdown (also filled from Raw_Currencies when present)
   * @param {number[]} _preselectedPartyIds reserved (unused)
   * @param {{ groupEntities?: {id:number,name:string}[], profiles?: {profileId:number,name:string}[] }} [options]
   */
  async buildControlSheet(defaultYear, partyNames = [], currencyCodes = [], _preselectedPartyIds = [], options = {}) {
    const groupEntities = options.groupEntities || [];
    const profiles = options.profiles || [];
    const sheetName = CONFIG.SHEETS.CONFIG_SHEET;
    const NR = CONFIG.NAMED_RANGES;

    await Excel.run(async (context) => {

      // ── Palette ────────────────────────────────────────────────────────────
      const NAVY       = '#1F4E79';
      const SEC_BG     = '#EBF3FB';
      const SEC_FG     = '#1F3864';
      const ACCENT     = '#2E75B6';
      const LABEL_C    = '#595959';
      const WHITE      = '#FFFFFF';

      // ── 1. Get or create sheet ─────────────────────────────────────────────
      let sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
      sheet.load('isNullObject');
      await context.sync();
      if (sheet.isNullObject) {
        sheet = context.workbook.worksheets.add(sheetName);
        sheet.position = 0;
      } else {
        const used = sheet.getUsedRangeOrNullObject();
        used.load('isNullObject');
        await context.sync();
        // Clear full width so legacy K:L party lists from older builds are removed
        if (!used.isNullObject) sheet.getRange('A:Z').clear(Excel.ClearApplyTo.all);
      }
      await context.sync();

      // ── 2. Delete old cfg_* named ranges ───────────────────────────────────
      for (const name of Object.values(NR)) {
        const item = context.workbook.names.getItemOrNullObject(name);
        item.load('isNullObject');
        await context.sync();
        if (!item.isNullObject) { item.delete(); await context.sync(); }
      }

      const c = (addr) => sheet.getRange(addr);

      // ── 3. Column widths (in POINTS — 1pt ≈ 1.33px) ──────────────────────
      //  A = left margin
      //  B = label col 1       C = value col 1
      //  D = gap
      //  E = label col 2       F = value col 2
      const CW = { A:8, B:130, C:180, D:20, E:130, F:180 };
      for (const [col, w] of Object.entries(CW)) c(`${col}:${col}`).format.columnWidth = w;
      await context.sync();

      // ── Helpers ────────────────────────────────────────────────────────────
      const sectionHeader = (addr, text) => {
        const r = c(addr);
        r.merge(false);
        c(addr.split(':')[0]).values = [[`  ${text}`]];
        r.format.fill.color            = SEC_BG;
        r.format.font.color            = SEC_FG;
        r.format.font.bold             = true;
        r.format.font.size             = 10;
        r.format.rowHeight             = 26;
        r.format.verticalAlignment     = 'Center';
        r.format.horizontalAlignment   = 'Left';
        r.format.borders.getItem('EdgeBottom').style = 'Continuous';
        r.format.borders.getItem('EdgeBottom').color = ACCENT;
      };

      const label = (addr, text) => {
        c(addr).values = [[text]];
        c(addr).format.font.size             = 10;
        c(addr).format.font.color            = LABEL_C;
        c(addr).format.font.bold             = true;
        c(addr).format.fill.color            = WHITE;
        c(addr).format.verticalAlignment     = 'Center';
        c(addr).format.horizontalAlignment   = 'Left';
      };

      const input = (addr, val) => {
        const r = c(addr);
        r.values = [[val]];
        r.format.fill.color            = WHITE;
        r.format.font.size             = 11;
        r.format.rowHeight             = 30;
        r.format.verticalAlignment     = 'Center';
        r.format.horizontalAlignment   = 'Left';
        r.format.borders.getItem('EdgeBottom').style = 'Continuous';
        r.format.borders.getItem('EdgeBottom').color = ACCENT;
        r.format.borders.getItem('EdgeTop').style    = 'Continuous';
        r.format.borders.getItem('EdgeTop').color    = '#D0D0D0';
        r.format.borders.getItem('EdgeLeft').style   = 'Continuous';
        r.format.borders.getItem('EdgeLeft').color   = '#D0D0D0';
        r.format.borders.getItem('EdgeRight').style  = 'Continuous';
        r.format.borders.getItem('EdgeRight').color  = '#D0D0D0';
        return r;
      };

      // ══════════════════════════════════════════════════════════════════════
      //  ROW 1 — TITLE
      // ══════════════════════════════════════════════════════════════════════
      const titleR = c('A1:F1');
      titleR.merge(false);
      c('A1').values = [['   Financial Report  —  Filter Panel']];
      titleR.format.fill.color           = NAVY;
      titleR.format.font.color           = WHITE;
      titleR.format.font.bold            = true;
      titleR.format.font.size            = 16;
      titleR.format.rowHeight            = 44;
      titleR.format.verticalAlignment    = 'Center';
      titleR.format.horizontalAlignment  = 'Left';
      await context.sync();

      // ROW 2 — spacer
      c('2:2').format.rowHeight = 8;

      // ══════════════════════════════════════════════════════════════════════
      //  ROW 3 — COMPANY & PERIOD section header
      // ══════════════════════════════════════════════════════════════════════
      sectionHeader('B3:F3', 'COMPANY & PERIOD');
      await context.sync();

      // ROW 4 — Labels + values: Companies, Year
      c('4:4').format.rowHeight = 30;
      label('B4', 'Companies');
      const compCell = input('C4', 'All Companies');
      // Dropdown references hidden PARTY_PICK_SHEET after party rows are written
      context.workbook.names.add(NR.SELECTED_COMPANIES, compCell);

      label('E4', 'Year');
      const yearCell = input('F4', defaultYear);
      yearCell.format.horizontalAlignment = 'Center';
      yearCell.dataValidation.rule = { list: { inCellDropDown: true, source: '"2020,2021,2022,2023,2024,2025,2026,2027,2028,2029,2030"' } };
      try {
        yearCell.dataValidation.prompt = {
          showPrompt: true,
          title: 'Year',
          message: 'Click the drop-down arrow, then choose the reporting year.',
        };
      } catch { /* optional on older hosts */ }
      context.workbook.names.add(NR.YEAR, yearCell);
      await context.sync();

      // ROW 5 — Labels + values: Group Entity, Frequency
      c('5:5').format.rowHeight = 30;
      label('B5', 'Group Entity');
      const groupCell = input('C5', 'None');
      context.workbook.names.add(NR.GROUP_ENTITY, groupCell);

      label('E5', 'Frequency');
      const freqCell = input('F5', 'Quarterly');
      freqCell.dataValidation.rule = { list: { inCellDropDown: true, source: '"Monthly,Quarterly,Half-year,Full year"' } };
      try {
        freqCell.dataValidation.prompt = {
          showPrompt: true,
          title: 'Frequency',
          message: 'Open the list to choose how months are grouped (e.g. Quarterly).',
        };
      } catch { /* optional */ }
      context.workbook.names.add(NR.FREQUENCY, freqCell);
      await context.sync();

      // ROW 6 — spacer
      c('6:6').format.rowHeight = 8;

      // ══════════════════════════════════════════════════════════════════════
      //  ROW 7 — REPORT SETTINGS section header
      // ══════════════════════════════════════════════════════════════════════
      sectionHeader('B7:F7', 'REPORT SETTINGS');
      await context.sync();

      // ROW 8 — Report Currency, Numbers In
      c('8:8').format.rowHeight = 30;
      label('B8', 'Report Currency');
      const currCell = input('C8', '');
      let currencyListReady = false;
      try {
        const currTable = context.workbook.tables.getItemOrNullObject('tbl_RawCurrencies');
        currTable.load('isNullObject');
        await context.sync();
        if (!currTable.isNullObject) {
          const body = currTable.getDataBodyRange();
          body.load('rowCount');
          await context.sync();
          if (body.rowCount > 0) {
            currCell.dataValidation.rule = {
              list: { inCellDropDown: true, source: '=INDIRECT("tbl_RawCurrencies[Code]")' }
            };
            currencyListReady = true;
          }
        }
        if (!currencyListReady && currencyCodes.length > 0) {
          const s = currencyCodes.slice(0, 50).join(',');
          if (s.length <= 255) {
            currCell.dataValidation.rule = { list: { inCellDropDown: true, source: `"${s}"` } };
            currencyListReady = true;
          }
        }
      } catch {
        if (currencyCodes.length > 0) {
          const s = currencyCodes.slice(0, 50).join(',');
          if (s.length <= 255) {
            currCell.dataValidation.rule = { list: { inCellDropDown: true, source: `"${s}"` } };
            currencyListReady = true;
          }
        }
      }
      try {
        currCell.dataValidation.prompt = {
          showPrompt: true,
          title: 'Report currency',
          message: currencyListReady
            ? 'Click the arrow to pick a currency code.'
            : 'List fills after you use “Download reference data” in the task pane (or type a code manually).',
        };
      } catch { /* optional */ }
      context.workbook.names.add(NR.REPORT_CURRENCY, currCell);

      label('E8', 'Numbers In');
      const numsCell = input('F8', 'Whole');
      numsCell.dataValidation.rule = { list: { inCellDropDown: true, source: '"Whole,Thousands,Millions"' } };
      try {
        numsCell.dataValidation.prompt = {
          showPrompt: true,
          title: 'Numbers in',
          message: 'Choose how amounts are scaled in the built reports.',
        };
      } catch { /* optional */ }
      context.workbook.names.add(NR.NUMBERS_IN, numsCell);
      await context.sync();

      // ROW 9 — Period, Profile ID
      c('9:9').format.rowHeight = 30;
      label('B9', 'Period');
      const periodCell = input('C9', 'Full year (0)');
      periodCell.format.horizontalAlignment = 'Left';
      try {
        periodCell.dataValidation.prompt = {
          showPrompt: true,
          title: 'Period',
          message: 'Depends on Frequency (e.g. Q1–Q4 when Quarterly). Pick from the list.',
        };
      } catch { /* optional */ }
      context.workbook.names.add(NR.PERIOD, periodCell);

      label('E9', 'Profile');
      const defaultProf = profiles.length > 0
        ? formatPickLabel(profiles[0].name ?? 'Profile', profiles[0].profileId ?? profiles[0].ProfileId ?? CONFIG.API.DEFAULT_PROFILE_ID)
        : formatPickLabel('Default', CONFIG.API.DEFAULT_PROFILE_ID);
      const profileCell = input('F9', defaultProf);
      profileCell.format.horizontalAlignment = 'Left';
      try {
        profileCell.dataValidation.prompt = {
          showPrompt: true,
          title: 'Master chart profile',
          message: 'Choose which mapped chart of accounts profile to use for reports.',
        };
      } catch { /* optional */ }
      context.workbook.names.add(NR.PROFILE_ID, profileCell);
      await context.sync();

      // ROW 10 — Sync Scope, Last Sync
      c('10:10').format.rowHeight = 30;
      label('B10', 'Sync Scope');
      const scopeCell = input('C10', 'TrialBalance');
      scopeCell.dataValidation.rule = { list: { inCellDropDown: true, source: '"All,TrialBalance,Parties,Currencies,Eliminations,Ownership,MasterChart"' } };
      try {
        scopeCell.dataValidation.prompt = {
          showPrompt: true,
          title: 'Sync scope',
          message: 'Default is Trial balance only. Choose All only for a full API refresh. Re-download uses this list.',
        };
      } catch { /* optional */ }
      context.workbook.names.add(NR.SYNC_SCOPE, scopeCell);

      label('E10', 'Last Sync');
      const tsCell = input('F10', 'Not synced yet');
      tsCell.format.font.italic = true;
      tsCell.format.font.color  = '#888888';
      context.workbook.names.add(NR.LAST_SYNC_TIME, tsCell);
      await context.sync();

      // ROW 11 — Reports to build + in-sheet status
      c('11:11').format.rowHeight = 30;
      label('B11', 'Reports to build');
      const reportPackCell = input('C11', 'Trial balance only');
      reportPackCell.dataValidation.rule = {
        list: {
          inCellDropDown: true,
          source: '"Trial balance only,Profit & Loss,Balance sheet,Cash flow,All financial reports"',
        },
      };
      try {
        reportPackCell.dataValidation.prompt = {
          showPrompt: true,
          title: 'Reports to build',
          message: 'Pick which statements to generate. All financial reports = P&L, Balance Sheet, and Cash Flow.',
        };
      } catch { /* optional */ }
      context.workbook.names.add(NR.REPORT_PACK, reportPackCell);

      label('E11', 'Status');
      const syncStatusCell = input('F11', 'Ready');
      syncStatusCell.format.font.italic = true;
      syncStatusCell.format.font.color  = '#444444';
      context.workbook.names.add(NR.SYNC_STATUS, syncStatusCell);
      await context.sync();

      // ROW 12 — spacer
      c('12:12').format.rowHeight = 10;

      // ══════════════════════════════════════════════════════════════════════
      //  ROW 13 — SYNC DATA button (green, clickable via onSelectionChanged)
      // ══════════════════════════════════════════════════════════════════════
      const syncBtn = c('B13:C13');
      syncBtn.merge(false);
      c('B13').values = [['⟳   Sync Data']];
      syncBtn.format.fill.color          = '#107C10';
      syncBtn.format.font.color          = WHITE;
      syncBtn.format.font.bold           = true;
      syncBtn.format.font.size           = 14;
      syncBtn.format.rowHeight           = 40;
      syncBtn.format.verticalAlignment   = 'Center';
      syncBtn.format.horizontalAlignment = 'Center';
      await context.sync();

      const hintR = c('E13:F13');
      hintR.merge(false);
      c('E13').values = [['Click the green button to apply filters and refresh your report from raw data. A status message appears in Status (F11) while working.']];
      hintR.format.font.size           = 10;
      hintR.format.font.italic         = true;
      hintR.format.font.color          = '#555555';
      hintR.format.rowHeight           = 40;
      hintR.format.verticalAlignment   = 'Center';
      hintR.format.horizontalAlignment = 'Left';
      hintR.format.wrapText            = true;
      await context.sync();

      // ROW 14 — spacer
      c('14:14').format.rowHeight = 8;

      // ══════════════════════════════════════════════════════════════════════
      //  Party names for dropdown — hidden sheet only (no K:L list on _Config)
      // ══════════════════════════════════════════════════════════════════════
      const pickName = CONFIG.SHEETS.PARTY_PICK_SHEET;
      if (partyNames.length > 0) {
        let pickSheet = context.workbook.worksheets.getItemOrNullObject(pickName);
        pickSheet.load('isNullObject');
        await context.sync();
        if (pickSheet.isNullObject) {
          pickSheet = context.workbook.worksheets.add(pickName);
        } else {
          const pickUsed = pickSheet.getUsedRangeOrNullObject();
          pickUsed.load('isNullObject');
          await context.sync();
          if (!pickUsed.isNullObject) pickUsed.clear(Excel.ClearApplyTo.all);
        }
        pickSheet.getRange('A1').values = [['PartyId']];
        pickSheet.getRange('B1').values = [['Name']];
        pickSheet.getRange('A1:B1').format.font.bold = true;
        pickSheet.getRange('A1:B1').format.fill.color = NAVY;
        pickSheet.getRange('A1:B1').format.font.color = WHITE;

        pickSheet.getRange('A2').values = [['*']];
        pickSheet.getRange('B2').values = [['All Companies']];

        const rows = partyNames.slice(0, 200).map(p => [
          p.partyId ?? '',
          formatPickLabel(p.name ?? '', p.partyId ?? ''),
        ]);
        const lastLookupRow = 2 + rows.length;
        if (rows.length > 0) {
          pickSheet.getRange(`A3:B${lastLookupRow}`).values = rows;
        }
        try {
          pickSheet.visibility = Excel.SheetVisibility.hidden;
        } catch { /* older hosts */ }
        await context.sync();

        const pickSource = `='${pickName}'!$B$2:$B$${lastLookupRow}`;
        try {
          compCell.dataValidation.rule = {
            list: { inCellDropDown: true, source: pickSource }
          };
          try {
            compCell.dataValidation.prompt = {
              showPrompt: true,
              title: 'Companies',
              message: 'Click the drop-down arrow (▼) to open the list. Pick a company or “All Companies”. Names include (Party ID) so filters match the right company.',
            };
          } catch { /* optional */ }
          await context.sync();
        } catch (e) {
          console.warn('[buildControlSheet] Company dropdown failed (non-critical):', e.message);
        }
      }
      await context.sync();

      // ── Hidden pick lists: group entities (not companies), period labels, profiles ──
      const escSheet = (n) => String(n).replace(/'/g, "''");
      const groupPickName = CONFIG.SHEETS.GROUP_PICK_SHEET;
      let groupPickSheet = context.workbook.worksheets.getItemOrNullObject(groupPickName);
      groupPickSheet.load('isNullObject');
      await context.sync();
      if (groupPickSheet.isNullObject) {
        groupPickSheet = context.workbook.worksheets.add(groupPickName);
      } else {
        const gu = groupPickSheet.getUsedRangeOrNullObject();
        gu.load('isNullObject');
        await context.sync();
        if (!gu.isNullObject) gu.clear(Excel.ClearApplyTo.all);
      }
      groupPickSheet.getRange('A1').values = [['GroupEntityId']];
      groupPickSheet.getRange('B1').values = [['Label']];
      groupPickSheet.getRange('A1:B1').format.font.bold = true;
      groupPickSheet.getRange('A1:B1').format.fill.color = NAVY;
      groupPickSheet.getRange('A1:B1').format.font.color = WHITE;
      groupPickSheet.getRange('A2').values = [['']];
      groupPickSheet.getRange('B2').values = [['None']];
      let gLastRow = 2;
      const geRows = (groupEntities || []).filter(g => g && (g.id != null || g.Id != null)).slice(0, 200);
      if (geRows.length > 0) {
        const gdata = geRows.map((g) => {
          const id = g.id ?? g.Id;
          const nm = g.name ?? g.Name ?? `Group ${id}`;
          return [id, formatPickLabel(nm, id)];
        });
        gLastRow = 2 + gdata.length;
        groupPickSheet.getRange(`A3:B${gLastRow}`).values = gdata;
      }
      try {
        groupPickSheet.visibility = Excel.SheetVisibility.hidden;
      } catch { /* older hosts */ }
      await context.sync();

      const groupPickSrc = `='${escSheet(groupPickName)}'!$B$2:$B$${gLastRow}`;
      try {
        groupCell.dataValidation.rule = { list: { inCellDropDown: true, source: groupPickSrc } };
        try {
          groupCell.dataValidation.prompt = {
            showPrompt: true,
            title: 'Group entity',
            message: 'Optional consolidation group from your tenant — not the company list. Choose None to filter by Companies only.',
          };
        } catch { /* optional */ }
        await context.sync();
      } catch (e) {
        console.warn('[buildControlSheet] Group entity dropdown failed (non-critical):', e.message);
      }

      const periodPickName = CONFIG.SHEETS.PERIOD_PICK_SHEET;
      let periodPickSheet = context.workbook.worksheets.getItemOrNullObject(periodPickName);
      periodPickSheet.load('isNullObject');
      await context.sync();
      if (periodPickSheet.isNullObject) {
        periodPickSheet = context.workbook.worksheets.add(periodPickName);
      } else {
        const pu = periodPickSheet.getUsedRangeOrNullObject();
        pu.load('isNullObject');
        await context.sync();
        if (!pu.isNullObject) pu.clear(Excel.ClearApplyTo.all);
      }
      const defaultFreqPt = freqToPeriodType('Quarterly');
      const periodLabels = buildPeriodPickLabels(defaultFreqPt);
      periodPickSheet.getRange(`A1:A${periodLabels.length}`).values = periodLabels;
      try {
        periodPickSheet.visibility = Excel.SheetVisibility.hidden;
      } catch { /* older hosts */ }
      await context.sync();
      const periodLast = periodLabels.length;
      const periodPickSrc = `='${escSheet(periodPickName)}'!$A$1:$A$${periodLast}`;
      try {
        periodCell.dataValidation.rule = { list: { inCellDropDown: true, source: periodPickSrc } };
        await context.sync();
      } catch (e) {
        console.warn('[buildControlSheet] Period dropdown failed (non-critical):', e.message);
      }

      const profilePickName = CONFIG.SHEETS.PROFILE_PICK_SHEET;
      let profilePickSheet = context.workbook.worksheets.getItemOrNullObject(profilePickName);
      profilePickSheet.load('isNullObject');
      await context.sync();
      if (profilePickSheet.isNullObject) {
        profilePickSheet = context.workbook.worksheets.add(profilePickName);
      } else {
        const pru = profilePickSheet.getUsedRangeOrNullObject();
        pru.load('isNullObject');
        await context.sync();
        if (!pru.isNullObject) pru.clear(Excel.ClearApplyTo.all);
      }
      profilePickSheet.getRange('A1').values = [['ProfileId']];
      profilePickSheet.getRange('B1').values = [['Label']];
      profilePickSheet.getRange('A1:B1').format.font.bold = true;
      profilePickSheet.getRange('A1:B1').format.fill.color = NAVY;
      profilePickSheet.getRange('A1:B1').format.font.color = WHITE;
      let prLastRow = 1;
      const profRows = (profiles || []).filter(p => p && (p.profileId != null || p.ProfileId != null)).slice(0, 100);
      if (profRows.length > 0) {
        const pdata = profRows.map((p) => {
          const id = p.profileId ?? p.ProfileId ?? CONFIG.API.DEFAULT_PROFILE_ID;
          const nm = p.name ?? p.Name ?? `Profile ${id}`;
          return [id, formatPickLabel(nm, id)];
        });
        prLastRow = 1 + pdata.length;
        profilePickSheet.getRange(`A2:B${prLastRow}`).values = pdata;
      } else {
        profilePickSheet.getRange('A2').values = [[CONFIG.API.DEFAULT_PROFILE_ID]];
        profilePickSheet.getRange('B2').values = [[formatPickLabel('Default', CONFIG.API.DEFAULT_PROFILE_ID)]];
        prLastRow = 2;
      }
      try {
        profilePickSheet.visibility = Excel.SheetVisibility.hidden;
      } catch { /* older hosts */ }
      await context.sync();
      const profilePickSrc = `='${escSheet(profilePickName)}'!$B$2:$B$${prLastRow}`;
      try {
        profileCell.dataValidation.rule = { list: { inCellDropDown: true, source: profilePickSrc } };
        await context.sync();
      } catch (e) {
        console.warn('[buildControlSheet] Profile dropdown failed (non-critical):', e.message);
      }

      await context.sync();

      // ══════════════════════════════════════════════════════════════════════
      //  ROW 16 — REPORTS section header
      // ══════════════════════════════════════════════════════════════════════
      sectionHeader('B16:F16', 'REPORTS');
      await context.sync();

      // ROW 17 — Report navigation buttons (hyperlinks to report sheets)
      c('17:17').format.rowHeight = 34;
      const navBtns = [
        { cell: 'B17', label: 'Trial Balance', sheet: CONFIG.SHEETS.RPT_TRIAL_BALANCE },
        { cell: 'C17', label: 'Profit & Loss', sheet: CONFIG.SHEETS.RPT_PROFIT_LOSS },
        { cell: 'E17', label: 'Balance Sheet', sheet: CONFIG.SHEETS.RPT_BALANCE_SHEET },
        { cell: 'F17', label: 'Cash Flow',     sheet: CONFIG.SHEETS.RPT_CASH_FLOW },
      ];
      for (const btn of navBtns) {
        const r = c(btn.cell);
        r.values = [[btn.label]];
        r.format.fill.color          = ACCENT;
        r.format.font.bold           = true;
        r.format.font.size           = 11;
        r.format.horizontalAlignment = 'Center';
        r.format.verticalAlignment   = 'Center';
        r.hyperlink = {
          documentReference: `'${btn.sheet}'!A1`,
          screenTip: `Go to ${btn.label} report`,
          textToDisplay: btn.label,
        };
      }
      await context.sync();

      // Re-apply white font AFTER sync so it overrides Excel's hyperlink style
      for (const btn of navBtns) {
        c(btn.cell).format.font.color     = WHITE;
        c(btn.cell).format.font.underline = 'None';
      }
      await context.sync();

      c('B18').values = [['Click any button above to navigate to that report. Run "Sync Data" first to generate reports.']];
      c('B18:F18').merge(false);
      c('B18').format.font.size   = 9;
      c('B18').format.font.italic = true;
      c('B18').format.font.color  = '#888888';
      c('B18').format.wrapText    = true;
      c('18:18').format.rowHeight = 24;
      await context.sync();

      // ── Freeze row 1, activate ─────────────────────────────────────────
      sheet.freezePanes.freezeRows(1);
      sheet.activate();
      await context.sync();
    });
  }

  /**
   * Read an Excel table's header + body in small row batches.
   * Avoids a single huge `values` load that exceeds Office.js payload limits (≈5MB on Excel for the web).
   * @param {Excel.RequestContext} context
   * @param {Excel.Table} table
   * @param {number} [chunkRows=2000]
   * @returns {Promise<{ headers: string[], rows: any[][] }>}
   */
  async _readTableBodyInChunks(context, table, chunkRows = 2000) {
    const hdrRange = table.getHeaderRowRange();
    const bodyRange = table.getDataBodyRange();
    hdrRange.load('values');
    bodyRange.load(['rowCount', 'columnCount']);
    await context.sync();

    const headers = hdrRange.values[0];
    const rowCount = bodyRange.rowCount;
    const colCount = bodyRange.columnCount;
    const rows = [];
    if (!rowCount || !colCount || rowCount <= 0) {
      return { headers, rows };
    }

    for (let start = 0; start < rowCount; start += chunkRows) {
      const h = Math.min(chunkRows, rowCount - start);
      const topLeft = bodyRange.getCell(start, 0);
      const chunkRange = topLeft.getResizedRange(h - 1, colCount - 1);
      chunkRange.load('values');
      await context.sync();
      const vals = chunkRange.values;
      if (vals && vals.length) {
        for (let i = 0; i < vals.length; i++) rows.push(vals[i]);
      }
    }
    return { headers, rows };
  }

  /**
   * Sync from raw data already in Excel — reads Raw_TrialBalance + Raw_MasterAccounts (+ mappings),
   * joins and filters by _Config settings, then builds report sheet(s) per cfg.allFinancialReports.
   * Does NOT call any API — works entirely from data already in the workbook.
   */
  async syncFromRawData(cfg) {
    await Excel.run(async (context) => {
      const tbTable = context.workbook.tables.getItemOrNullObject('tbl_RawTrialBalance');
      tbTable.load('isNullObject');
      await context.sync();
      if (tbTable.isNullObject) {
        throw new Error('Raw_TrialBalance table not found. Click "Setup Workbook" first to download data.');
      }

      const { headers: tbHeaders, rows: tbRows } = await this._readTableBodyInChunks(context, tbTable);

      const maTable = context.workbook.tables.getItemOrNullObject('tbl_RawMasterAccounts');
      maTable.load('isNullObject');
      await context.sync();

      let maHeaders = [];
      let maRows = [];
      if (!maTable.isNullObject) {
        const ma = await this._readTableBodyInChunks(context, maTable);
        maHeaders = ma.headers;
        maRows = ma.rows;
      } else {
        console.warn('[syncFromRawData] tbl_RawMasterAccounts not found — reports will lack section classification.');
      }

      let partyHeaders = [];
      let partyRows = [];
      const partyTable = context.workbook.tables.getItemOrNullObject('tbl_RawParties');
      partyTable.load('isNullObject');
      await context.sync();
      if (!partyTable.isNullObject) {
        const parties = await this._readTableBodyInChunks(context, partyTable);
        partyHeaders = parties.headers;
        partyRows = parties.rows;
      } else {
        console.warn('[syncFromRawData] tbl_RawParties not found — Party Name column will be blank unless Raw_TrialBalance has it.');
      }

      if (cfg.reportCurrency && String(cfg.reportCurrency).trim() !== '') {
        console.warn(
          '[syncFromRawData] Report currency is set on _Config but the add-in builds reports from raw amounts in source currency (same as GetTrialBalanceFlat). For numbers to match Razor, use the same report currency on the web or leave report currency blank in Excel.'
        );
      }

      const joined = reportEngine.joinData(tbRows, tbHeaders, maRows, maHeaders, {
        profileId: cfg.profileId,
        partyRows,
        partyHeaders,
      });
      const filtered = reportEngine.filterRows(joined, cfg);
      const yearPartyRows = reportEngine.filterRowsYearAndParties(joined, cfg);
      const displayMonths = reportEngine.getDisplayMonths(
        cfg.year, cfg.periodType, cfg.period
      );

      if (displayMonths.length === 0) {
        throw new Error('No display months resolved for the selected period. Check Year / Frequency / Period on _Config.');
      }

      const flags = resolveFinancialReportFlags(cfg);
      console.log(
        `[syncFromRawData] reports P/L=${flags.profitLoss} BS=${flags.balanceSheet} CF=${flags.cashFlow} year=${cfg.year}, partyIds=${cfg.partyIds?.length || 0}`
      );
      console.log(`[syncFromRawData] ${tbRows.length} raw rows → ${filtered.length} after filters, displayMonths=[${displayMonths}]`);

      const tbReport = reportEngine.buildTrialBalance(filtered, displayMonths, cfg.numbersIn);
      await reportWriter.writeTrialBalanceReport(
        context, CONFIG.SHEETS.RPT_TRIAL_BALANCE, tbReport, cfg
      );

      const placeholder = (kind) =>
        `Run Sync with Report set to “${kind}” or “All financial reports” on _Config (or the task pane), then try again.`;

      if (flags.profitLoss) {
        const pnlReport = reportEngine.buildProfitLoss(filtered, displayMonths, cfg.numbersIn);
        await reportWriter.writeHierarchicalReport(
          context, CONFIG.SHEETS.RPT_PROFIT_LOSS, pnlReport, cfg
        );
      } else {
        await reportWriter.writePlaceholderReport(context, CONFIG.SHEETS.RPT_PROFIT_LOSS, placeholder('Profit & Loss'));
      }

      if (flags.balanceSheet) {
        const bsReport = reportEngine.buildBalanceSheet(filtered, displayMonths, cfg.numbersIn, {
          year: cfg.year,
          retainedEarningsRows: yearPartyRows,
        });
        await reportWriter.writeHierarchicalReport(
          context, CONFIG.SHEETS.RPT_BALANCE_SHEET, bsReport, cfg
        );
      } else {
        await reportWriter.writePlaceholderReport(context, CONFIG.SHEETS.RPT_BALANCE_SHEET, placeholder('Balance sheet'));
      }

      if (flags.cashFlow) {
        const cfReport = reportEngine.buildCashFlow(filtered, displayMonths, cfg.numbersIn);
        await reportWriter.writeHierarchicalReport(
          context, CONFIG.SHEETS.RPT_CASH_FLOW, cfReport, cfg
        );
      } else {
        await reportWriter.writePlaceholderReport(context, CONFIG.SHEETS.RPT_CASH_FLOW, placeholder('Cash flow'));
      }

      await this._positionReportSheetsAfterConfigInContext(context);

      const targetName = getActivateReportSheetName(cfg);
      const rptSheet = context.workbook.worksheets.getItemOrNullObject(targetName);
      rptSheet.load('isNullObject');
      await context.sync();
      if (!rptSheet.isNullObject) {
        rptSheet.activate();
      } else {
        const fallback = context.workbook.worksheets.getItemOrNullObject(CONFIG.SHEETS.RPT_TRIAL_BALANCE);
        fallback.load('isNullObject');
        await context.sync();
        if (!fallback.isNullObject) fallback.activate();
      }
      await context.sync();
    });
  }

  /**
   * Move Rpt_* report tabs to sit immediately after _Config (user-requested tab order).
   * @param {Excel.RequestContext} context
   */
  async _positionReportSheetsAfterConfigInContext(context) {
    const configName = CONFIG.SHEETS.CONFIG_SHEET;
    const reportNames = [
      CONFIG.SHEETS.RPT_TRIAL_BALANCE,
      CONFIG.SHEETS.RPT_PROFIT_LOSS,
      CONFIG.SHEETS.RPT_BALANCE_SHEET,
      CONFIG.SHEETS.RPT_CASH_FLOW
    ];
    const cfgSheet = context.workbook.worksheets.getItemOrNullObject(configName);
    cfgSheet.load('isNullObject');
    await context.sync();
    if (cfgSheet.isNullObject) return;

    const sheets = context.workbook.worksheets;
    sheets.load('items/name');
    await context.sync();
    let configIdx = -1;
    for (let i = 0; i < sheets.items.length; i++) {
      if (sheets.items[i].name === configName) {
        configIdx = i;
        break;
      }
    }
    if (configIdx < 0) return;

    let insertAt = configIdx + 1;
    for (const name of reportNames) {
      const sh = context.workbook.worksheets.getItemOrNullObject(name);
      sh.load('isNullObject');
      await context.sync();
      if (sh.isNullObject) continue;
      sh.position = insertAt;
      await context.sync();
      insertAt++;
    }
  }

  /**
   * Register a click handler on the _Config sheet's Sync button cell.
   * Listens for onSelectionChanged — when user clicks B13 (Sync Data), triggers syncFromRawData.
   * @param {Function} onSyncTriggered - callback(cfg) called when sync button is clicked
   */
  async registerSyncButton(onSyncTriggered) {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItemOrNullObject(CONFIG.SHEETS.CONFIG_SHEET);
        sheet.load('isNullObject');
        await context.sync();
        if (sheet.isNullObject) return;

        sheet.onSelectionChanged.add(async (event) => {
          if (event.address === 'B13' || event.address === 'C13' || event.address === 'B13:C13') {
            try {
              await onSyncTriggered();
            } catch (err) {
              console.error('[SyncButton] Error:', err);
            }
          }
        });
        await context.sync();
      });
    } catch (err) {
      console.error('[registerSyncButton] Failed:', err);
    }
  }

  /**
   * Write the current timestamp to cfg_LastSync.
   */
  async updateLastSyncTime() {
    try {
      await Excel.run(async (context) => {
        const item = context.workbook.names.getItemOrNullObject(CONFIG.NAMED_RANGES.LAST_SYNC_TIME);
        item.load('isNullObject');
        await context.sync();
        if (!item.isNullObject) {
          item.getRange().values = [[new Date().toLocaleString()]];
          await context.sync();
        }
      });
    } catch { /* non-critical */ }
  }

  /**
   * Updates the visible status cell on _Config (cfg_SyncStatus) so users see progress in-grid.
   * When loading=true, highlights the cell (amber) so long syncs are obvious in Excel.
   * @param {string} text
   * @param {{ loading?: boolean }} [options]
   */
  async setConfigStatusMessage(text, options = {}) {
    const loading = options.loading === true;
    try {
      await Excel.run(async (context) => {
        const item = context.workbook.names.getItemOrNullObject(CONFIG.NAMED_RANGES.SYNC_STATUS);
        item.load('isNullObject');
        await context.sync();
        if (!item.isNullObject) {
          const r = item.getRange();
          r.values = [[text ?? '']];
          if (loading) {
            r.format.fill.color = '#FFF4CE';
            r.format.font.bold = true;
            r.format.font.italic = false;
            r.format.font.color = '#1F3864';
          } else {
            r.format.fill.color = '#FFFFFF';
            r.format.font.bold = false;
            r.format.font.italic = true;
            r.format.font.color = '#444444';
          }
          await context.sync();
        }
      });
    } catch { /* non-critical */ }
  }

  /**
   * Re-apply _Config dropdowns from Raw_* tables and current Frequency (period list).
   * Call after "Download reference data" or when opening _Config from the task pane.
   */
  async refreshConfigDerivedValidation() {
    const NR = CONFIG.NAMED_RANGES;
    const escSheet = (n) => String(n).replace(/'/g, "''");
    await Excel.run(async (context) => {
      const wb = context.workbook;
      const cfgSheet = wb.worksheets.getItemOrNullObject(CONFIG.SHEETS.CONFIG_SHEET);
      cfgSheet.load('isNullObject');
      await context.sync();
      if (cfgSheet.isNullObject) return;

      const freqItem = wb.names.getItemOrNullObject(NR.FREQUENCY);
      freqItem.load('isNullObject');
      await context.sync();
      let freqStr = 'Quarterly';
      if (!freqItem.isNullObject) {
        const fr = freqItem.getRange();
        fr.load('values');
        await context.sync();
        const v = fr.values?.[0]?.[0];
        if (v != null && v !== '') freqStr = String(v).trim();
      }
      const pt = freqToPeriodType(freqStr);
      const periodLabels = buildPeriodPickLabels(pt);
      const periodPickName = CONFIG.SHEETS.PERIOD_PICK_SHEET;
      let periodPickSheet = wb.worksheets.getItemOrNullObject(periodPickName);
      periodPickSheet.load('isNullObject');
      await context.sync();
      if (periodPickSheet.isNullObject) {
        periodPickSheet = wb.worksheets.add(periodPickName);
      } else {
        const pu = periodPickSheet.getUsedRangeOrNullObject();
        pu.load('isNullObject');
        await context.sync();
        if (!pu.isNullObject) pu.clear(Excel.ClearApplyTo.all);
      }
      periodPickSheet.getRange(`A1:A${periodLabels.length}`).values = periodLabels;
      try {
        periodPickSheet.visibility = Excel.SheetVisibility.hidden;
      } catch { /* older hosts */ }
      await context.sync();

      const periodItem = wb.names.getItemOrNullObject(NR.PERIOD);
      periodItem.load('isNullObject');
      await context.sync();
      if (!periodItem.isNullObject) {
        const periodCell = periodItem.getRange();
        const periodLast = periodLabels.length;
        const periodPickSrc = `='${escSheet(periodPickName)}'!$A$1:$A$${periodLast}`;
        try {
          periodCell.dataValidation.rule = { list: { inCellDropDown: true, source: periodPickSrc } };
        } catch { /* */ }
        periodCell.load('values');
        await context.sync();
        const cur = periodCell.values?.[0]?.[0];
        const curStr = cur != null ? String(cur).trim() : '';
        const allowed = new Set(periodLabels.map((r) => r[0]));
        if (!allowed.has(curStr)) {
          periodCell.values = [['Full year (0)']];
        }
        await context.sync();
      }

      const currItem = wb.names.getItemOrNullObject(NR.REPORT_CURRENCY);
      currItem.load('isNullObject');
      await context.sync();
      if (!currItem.isNullObject) {
        const currCell = currItem.getRange();
        let currencyListReady = false;
        try {
          const currTable = wb.tables.getItemOrNullObject('tbl_RawCurrencies');
          currTable.load('isNullObject');
          await context.sync();
          if (!currTable.isNullObject) {
            const body = currTable.getDataBodyRange();
            body.load('rowCount');
            await context.sync();
            if (body.rowCount > 0) {
              currCell.dataValidation.rule = {
                list: { inCellDropDown: true, source: '=INDIRECT("tbl_RawCurrencies[Code]")' },
              };
              currencyListReady = true;
            }
          }
        } catch { /* */ }
        try {
          currCell.dataValidation.prompt = {
            showPrompt: true,
            title: 'Report currency',
            message: currencyListReady
              ? 'Click the arrow to pick a currency code.'
              : 'List fills after reference currencies are downloaded.',
          };
        } catch { /* */ }
        await context.sync();
      }

      const profItem = wb.names.getItemOrNullObject(NR.PROFILE_ID);
      profItem.load('isNullObject');
      await context.sync();
      if (!profItem.isNullObject) {
        const profileCell = profItem.getRange();
        const profilePickName = CONFIG.SHEETS.PROFILE_PICK_SHEET;
        let profilePickSheet = wb.worksheets.getItemOrNullObject(profilePickName);
        profilePickSheet.load('isNullObject');
        await context.sync();
        if (profilePickSheet.isNullObject) {
          profilePickSheet = wb.worksheets.add(profilePickName);
        } else {
          const pru = profilePickSheet.getUsedRangeOrNullObject();
          pru.load('isNullObject');
          await context.sync();
          if (!pru.isNullObject) pru.clear(Excel.ClearApplyTo.all);
        }
        profilePickSheet.getRange('A1').values = [['ProfileId']];
        profilePickSheet.getRange('B1').values = [['Label']];
        let prLastRow = 1;
        const mpTable = wb.tables.getItemOrNullObject('tbl_RawMasterProfiles');
        mpTable.load('isNullObject');
        await context.sync();
        if (!mpTable.isNullObject) {
          const hdr = mpTable.getHeaderRowRange();
          const body = mpTable.getDataBodyRange();
          hdr.load('values');
          body.load('values');
          await context.sync();
          const headers = hdr.values[0];
          const pidIdx = headers.indexOf('ProfileId');
          const nameIdx = headers.indexOf('Name');
          if (pidIdx >= 0 && nameIdx >= 0 && body.values) {
            const pdata = [];
            for (const row of body.values) {
              const id = row[pidIdx];
              const nm = row[nameIdx];
              if (id != null && id !== '') {
                pdata.push([id, formatPickLabel(nm ?? `Profile ${id}`, id)]);
              }
            }
            if (pdata.length > 0) {
              prLastRow = 1 + pdata.length;
              profilePickSheet.getRange(`A2:B${prLastRow}`).values = pdata;
            }
          }
        }
        if (prLastRow <= 1) {
          profilePickSheet.getRange('A2').values = [[CONFIG.API.DEFAULT_PROFILE_ID]];
          profilePickSheet.getRange('B2').values = [[formatPickLabel('Default', CONFIG.API.DEFAULT_PROFILE_ID)]];
          prLastRow = 2;
        }
        try {
          profilePickSheet.visibility = Excel.SheetVisibility.hidden;
        } catch { /* older hosts */ }
        await context.sync();
        const profilePickSrc = `='${escSheet(profilePickName)}'!$B$2:$B$${prLastRow}`;
        try {
          profileCell.dataValidation.rule = { list: { inCellDropDown: true, source: profilePickSrc } };
        } catch { /* */ }
        profileCell.load('values');
        await context.sync();
        const curP = profileCell.values?.[0]?.[0];
        const curPStr = curP != null ? String(curP).trim() : '';
        if (!curPStr || parseTrailingParenId(curPStr) == null) {
          const b2 = profilePickSheet.getRange('B2');
          b2.load('values');
          await context.sync();
          profileCell.values = b2.values;
        }
        await context.sync();
      }

      await context.sync();
    });
  }

  /**
   * Reads PartyId → Name from tbl_RawParties. Returns {} if the table is missing.
   */
  async readPartyNameLookupFromRawTable() {
    return Excel.run(async (context) => {
      const tbl = context.workbook.tables.getItemOrNullObject('tbl_RawParties');
      tbl.load('isNullObject');
      await context.sync();
      if (tbl.isNullObject) return {};

      const hdr = tbl.getHeaderRowRange();
      const body = tbl.getDataBodyRange();
      hdr.load('values');
      body.load('values');
      await context.sync();

      return reportEngine.buildPartyNameLookup(body.values, hdr.values[0]);
    });
  }

  /**
   * Reads all PartyId values from the tbl_RawParties table.
   * Returns an array of numeric IDs, or empty array if table doesn't exist.
   */
  async readPartyIdsFromRawTable() {
    return Excel.run(async (context) => {
      const tbl = context.workbook.tables.getItemOrNullObject('tbl_RawParties');
      tbl.load('isNullObject');
      await context.sync();
      if (tbl.isNullObject) return [];

      const hdr = tbl.getHeaderRowRange();
      const body = tbl.getDataBodyRange();
      hdr.load('values');
      body.load('values');
      await context.sync();

      const headers = hdr.values[0];
      const pidIdx = headers.indexOf('PartyId');
      if (pidIdx < 0) return [];

      const ids = [];
      for (const row of body.values) {
        const v = Number(row[pidIdx]);
        if (v > 0) ids.push(v);
      }
      return ids;
    });
  }

  /**
   * Read all cfg_* named ranges from the _Config sheet.
   * Converts friendly display values (Quarterly → 3, Whole → 0, etc.)
   * to the numeric enums the API expects.
   */
  async readConfigValues() {
    return Excel.run(async (context) => {
      const wb = context.workbook;
      const NR = CONFIG.NAMED_RANGES;

      // Load all named ranges in one batch
      const namedItems = {};
      for (const [key, name] of Object.entries(NR)) {
        const item = wb.names.getItemOrNullObject(name);
        item.load('isNullObject');
        namedItems[key] = item;
      }
      await context.sync();

      // Read values from non-null named ranges
      const rangeValues = {};
      for (const [key, item] of Object.entries(namedItems)) {
        if (!item.isNullObject) {
          const r = item.getRange();
          r.load('values');
          rangeValues[key] = r;
        }
      }
      await context.sync();

      const get = (key) => {
        const r = rangeValues[key];
        if (!r) return null;
        const v = r.values?.[0]?.[0];
        return (v === undefined || v === '') ? null : v;
      };

      // Map Frequency label → periodType integer (use || to guard against NaN)
      const freqMap = { 'Monthly': 1, 'Quarterly': 3, 'Half-year': 6, 'Full year': 12 };
      const freqRaw = get('FREQUENCY');
      const freqStr = freqRaw != null ? String(freqRaw).trim() : '';
      const periodType = freqMap[freqStr] || parseInt(freqStr) || 12;

      // Map NumbersIn label → integer
      const numsMap = { 'Whole': 0, 'Thousands': 1, 'Millions': 2 };
      const numsRaw = get('NUMBERS_IN');
      const numsStr = numsRaw != null ? String(numsRaw).trim() : '';
      const numbersIn = numsMap[numsStr] ?? parseInt(numsStr) ?? 0;

      // Parse selected company — "All Companies", "Name (id)", comma-separated IDs, or legacy name-only
      const partyIdsRaw = get('SELECTED_COMPANIES');
      const partyIdsStr = partyIdsRaw ? String(partyIdsRaw).trim() : '';
      const isAllCompanies = !partyIdsStr
        || partyIdsStr.toLowerCase() === 'all companies'
        || partyIdsStr.toLowerCase() === 'none'
        || partyIdsStr.toLowerCase() === 'all';

      let partyIds = [];
      if (!isAllCompanies) {
        const fromParen = parseTrailingParenId(partyIdsStr);
        if (fromParen != null && fromParen > 0) {
          partyIds = [fromParen];
        } else {
          const numericIds = partyIdsStr.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n > 0);
          if (numericIds.length > 0) {
            partyIds = numericIds;
          } else {
            try {
              const pt = wb.tables.getItemOrNullObject('tbl_RawParties');
              pt.load('isNullObject');
              await context.sync();
              if (!pt.isNullObject) {
                const hdr = pt.getHeaderRowRange();
                const body = pt.getDataBodyRange();
                hdr.load('values');
                body.load('values');
                await context.sync();
                const headers = hdr.values[0];
                const nameIdx = headers.indexOf('Name');
                const pidIdx = headers.indexOf('PartyId');
                if (nameIdx >= 0 && pidIdx >= 0 && body.values) {
                  const want = partyIdsStr.toLowerCase();
                  for (const row of body.values) {
                    const name = String(row[nameIdx] ?? '').trim();
                    const namePick = formatPickLabel(name, row[pidIdx]).toLowerCase();
                    if (name.toLowerCase() === want || namePick === want) {
                      const pid = row[pidIdx];
                      if (pid != null && pid !== '') {
                        const n = Number(pid);
                        if (!isNaN(n) && n > 0) partyIds = [n];
                      }
                      break;
                    }
                  }
                }
              }
            } catch { /* non-critical */ }
          }
        }
      }

      // Group entity — "None" or "Name (id)" from _GroupPick; never reuse company list
      const groupRaw = get('GROUP_ENTITY');
      let groupEntityId = parseTrailingParenId(groupRaw);
      if (groupEntityId != null && groupEntityId > 0) {
        partyIds = [];
      } else {
        groupEntityId = null;
      }

      const reportPackRaw = get('REPORT_PACK');
      const reportPackStr = reportPackRaw != null ? String(reportPackRaw).trim() : '';
      const allFinancialReports = /all\s*financial/i.test(reportPackStr);

      const periodRaw = get('PERIOD');
      let period = parseTrailingParenId(periodRaw);
      if (period == null && periodRaw != null && periodRaw !== '') {
        const n = parseInt(String(periodRaw).trim(), 10);
        period = isNaN(n) ? 0 : n;
      }
      if (period == null || isNaN(period)) period = 0;

      const profileRaw = get('PROFILE_ID');
      let profileId = parseTrailingParenId(profileRaw);
      if (profileId == null || profileId <= 0) {
        const n = parseInt(String(profileRaw ?? '').trim(), 10);
        profileId = !isNaN(n) && n > 0 ? n : CONFIG.API.DEFAULT_PROFILE_ID;
      }

      return {
        year: parseInt(get('YEAR')) || new Date().getFullYear(),
        periodType,
        period,
        profileId,
        numbersIn,
        reportCurrency: (get('REPORT_CURRENCY') && String(get('REPORT_CURRENCY')).trim()) || null,
        // Default TrialBalance so "Re-download" does not pull everything unless user chooses All on _Config
        syncScope: String(get('SYNC_SCOPE') || 'TrialBalance'),
        reportPack: reportPackStr || 'Trial balance only',
        allFinancialReports,
        includeCompanyElimination: false,
        includeHistoricalData: false,
        partyIds,
        groupEntityId
      };
    });
  }

  /**
   * Simple CFO-style control sheet: sum of pivot Amount, balance check, rounding note.
   * Expects table Tbl_PivotTB on PivotData_TB (run pivot export first).
   */
  async writeCfoControlsSheet(overwriteSheets = true) {
    const sheetName = CONFIG.SHEETS.CFO_CONTROLS_SHEET;
    const tableName = CONFIG.EXCEL.PIVOT_TB_TABLE_NAME;

    await Excel.run(async (context) => {
      const worksheet = await this.getOrCreateWorksheet(context, sheetName);
      await this.clearWorksheet(context, worksheet, overwriteSheets);

      worksheet.getRange('A1').values = [['CFO control panel']];
      worksheet.getRange('A1').format.font.bold = true;
      worksheet.getRange('A1').format.font.size = 14;

      worksheet.getRange('A3').values = [['Net sum (trial balance / pivot Amount column)']];
      worksheet.getRange('B3').formulas = [[`=IFERROR(SUM(${tableName}[Amount]),"Create pivot export first")`]];

      worksheet.getRange('A4').values = [['Balance check']];
      worksheet.getRange('B4').formulas = [[`=IFERROR(IF(ABS(SUM(${tableName}[Amount]))<1,"OK — within 1 unit (rounding)","Review — net ≠ 0"),"Run Export pivot-ready trial balance first")`]];

      worksheet.getRange('A6').values = [['Kontroll diff (manual)']];
      worksheet.getRange('B6').values = [['']];
      worksheet.getRange('A7').values = [['Notes']];
      worksheet.getRange('B7').values = [['Compare to your consolidation tie-out. Øre / penny differences often clear with rounding rules.']];

      worksheet.getRange('B3:B4').numberFormat = [[CONFIG.EXCEL.NUMBER_FORMAT]];
      worksheet.getRange('A:A').format.columnWidth = 36;
      worksheet.getRange('B:B').format.columnWidth = 48;

      await context.sync();
    });
  }
}

// Export singleton instance
export const excelService = new ExcelService();